import { projectAdvisors, type UiAdvisors } from './advisors';
import { projectForecast, type UiForecast } from './forecast';
import { projectStaffRoster, projectStaffHireCosts } from './staff-roster';
import { preserveFailedSave, preserveReplacementSave, latestReplacementSave } from './failed-save';
import type { SlotInfo } from '../save/save-manager';
/**
 * Lane 3 <-> Lane 1/4 adapter. The UI ONLY talks to the sim through this file.
 * Live mode: tick/speed/commands run through Lane 1's worker client
 * (src/worker/client-factory.ts). Until Lane 4's systems are registered in the
 * worker, HUD collections (animals, staff, cash, alerts) come from the local
 * stub lists below; every live call degrades gracefully to the stub.
 */
import type { Speed, SimCommand } from '../contracts/sim';
import type { MonetizationHooks } from '../contracts/social';
import type { SimWorkerClient } from '../worker/bridge';

export type RarityTier = 'common' | 'uncommon' | 'rare' | 'exotic';
/** P210 knowledge layers (Mgmt-Sim #394, ui.mgmt detail animals): estimates
 *  with confidence until a paid reveal. Unknown = estimate at low confidence;
 *  known = true value at 100%. Costs feed the reveal buttons. */
export interface UiKnowledge {
  healthKnown: boolean; health: number; healthConfidenceBp: number;
  qualityKnown: boolean; qualityConfidenceBp: number;
  geneticsRecordAvailable?:boolean;
  geneticsKnown: boolean;
  costs: { inspectCents: number; vetCheckCents: number; dnaTestCents: number };
  /** P209 quality COMPONENTS (plan §13 inputs); only present once quality is
   *  known via inspection. The composite score is HELD pending the owner's
   *  formula decisions - the UI must not show a quality number. */
  qualityComponents?: { trophy: number | null; morphMultiplierBp: number; health: number; outbreeding: number; inbreedingBp: number; complete: boolean };
}
export interface UiAnimal {
  id: string; name: string; species: string; speciesId?: string; sex: 'M' | 'F';
  hybrid?: string | null;
  coat: string; temperament: string; traits: string[]; rare: boolean;
  health: number; stress: number; enclosure: string | null;
  /** Stub fields for the biome/rarity direction until field.ts data lands. */
  biome: string; rarity: RarityTier;
  /** Sim detail slice: sick (animal.treat applies). */
  ill: boolean;
  /** Birth tick from the live worker; undefined = the sim does not say (stubs, older workers). */
  bornTick?: number;
  maturityDays?:number;
  /** Sim detail slice when projected; undefined = the sim does not say. */
  quarantined?: boolean;
  /** P210: present once the worker runs Mgmt-Sim #394; undefined on older workers. */
  knowledge?: UiKnowledge;
}
export interface UiStaffHireCost { role: string; wagePerDayCents: number; }
export interface UiStaffRole { role: string; duty: string; count: number; portrait: 'ranger' | 'veterinarian' | 'scout' | 'keeper'; }
/**
 * Field-mode HUD data. STUB until Mgmt-Sim ships the sim side; the stealth
 * values code against the proposed FieldSlice extension (u8 noise,
 * u8 visibility, 0..1 after decode) - see PR discussion for the proposal.
 */
export interface UiFieldWorld {
  zoomBp?:number;
  player:{xM:number;zM:number;heading:number;crouched:boolean};
  animals:Array<{animalId:string;species:string;displayName:string;xM:number;zM:number;heading:number;sex:'M'|'F';ageDays:number;down:'sedated'|'held'|null;speedMmPerTick:number;spotted:boolean;trackable:boolean}>;
  traps:Array<{id:string;typeId:string;xM:number;zM:number;state:string;holdingAnimalId:string|null}>;
  noise:number;visibility:number;playerBiome:string;mapHalfExtentM:number;
}
export interface UiFieldState {
  /** Active-goal chip text (quest/objective data later). */
  goal: string | null;
  /** Traps left in the field kit; badge on SET TRAP. */
  trapsRemaining: number;
  /** 0..1: how much noise the player makes / how visible they are. */
  stealth: { noise: number; visibility: number };
}
export type RegionMissing =
  | { kind: 'parcels'; have: number; need: number }
  | { kind: 'reputation'; track: string; have: number; need: number }
  | { kind: 'license'; licenseId: string }
  | { kind: 'fee'; have: number; need: number };
export interface UiParcel { id: string; hectares: number; landClass: string; distanceKm: number; price: number; owned: boolean; }
/** 3A/4A/5A: per-parcel fence drawing grid (10 m cells). segments/gates are
 *  canonical edge keys 'x,y,N|W'; grades is parallel to segments (0 = legacy
 *  grade-less). Absent parcel id = older worker, drawing hidden. */
export interface UiFenceLayout { cols: number; rows: number; edgeM: number; segments: string[]; gates: string[]; grades?: number[]; /** P104: cells already claimed by an enclosure on this parcel. */ claimed?: number[]; }
export interface UiEnclosure { id: string; parcelId: string; type: string; areaHa: number; fencePerimeterM: number; waterPoints: number; animalCount: number; /** 4A: 1 basic / 2 heavy / 3 predator-proof; 0 or absent = legacy ungraded. */ fenceGrade?: number; vaccinated?: boolean; outbreakReported?: boolean; outbreakConcealed?: boolean; illCount?: number; }
export interface UiDealerOffer { id: string; species: string; sex: string; ageDays?: number; maturityDays?:number; health: number; priceCents: number; }
export interface UiStaffMember { id: string; role: string; assignedTo: string | null; fatigue: number; skill: number; wagePerDay: number; morale?: number | null; manager?: boolean; blockedUntilTick?: number; }
export interface UiTrap { id: string; parcelId: string; type: string; baited: boolean; /** ui.park (sim #210): tick it can be checked, and whether that has passed. */ readyTick?: number; ready?: boolean }
export interface UiLoan { principal: number; aprPercent: number; }
export interface UiMonthBucket {
  month: number; incomeCents: number; expenseCents: number; netCents: number;
  byCategory: Record<string, { incomeCents: number; expenseCents: number; netCents: number }>;
}
export interface UiContractOffer {
  fulfilledReputation?:number;lapsedReputation?:number;
  permitReward?:import('../sim/systems/types').LicenseKind; requires?:import('../sim/systems/goals').Requirement; penaltyRepTrack?:string; penaltyRepPoints?:number;
  deliveryRoster?:Array<{animalId:string;sex:string;health:number;inbreedingBp:number;blockedReason:string|null}>;

  id: string; counterparty: string; species: string; count: number; minHealth: number;
  pricePerHeadCents: number; deadlineTick: number; acceptedTick?: number;
  /** P505 (Mgmt-Sim #388): cash penalty if the contract lapses; 0 for permit
   *  jobs (reputation-only). Optional on older workers. */
  lapsePenaltyCents?: number;
}
export interface UiStudy { id: string; name: string; description: string; costCents: number; durationDays: number; }
export interface UiResearch {
  active: { studyId: string; startedTick: number; completesTick: number; progressBp: number } | null;
  completed: string[]; catalog: UiStudy[];
}
/** Sim-Core research tree (PR #326): ui.mgmt.research when it carries `tree`. Render-World builds the screen from state.researchTree. */
export interface UiResearchNode { id: string; name: string; description: string; branch: string; tier: number; costCents: number; baseCostCents?: number; discountBp?: number; durationDays: number; requires: string[]; missingPrereqs: string[]; excludes?: string[]; excludedBy?: string[]; effects: unknown; state: 'completed' | 'active' | 'available' | 'locked' | 'excluded' }
export interface UiResearchTree { active: { studyId: string; completesDay: number; remainingDays: number } | null; completed: string[]; tree: UiResearchNode[] }
/** Static catalog entry from data/enclosures.json (costs in cents). */
export interface UiEnclosureType { base_cost_cents: number; fence_cost_per_m_cents: number; water_point_cost_cents: number; density_per_ha: number; groups: string[]; }
/** Sim-Core identity projection. Labels are PROPOSED placeholders (data/paths.json v2), not approved copy. */
export interface UiIdentity { paths: Array<{ id: string; level: number; /** XP is parked at a rep gate: the track and score needed for the next level (requested from Sim-Core, optional). */ gate?: { track: string; need: number; level: number } }>; knownFor: Array<{ label: string; score: number }> }
/** Mgmt-Sim standing, licences, programs and tourism projections (ui.mgmt, stack #170-#220). All optional: older sims omit them. */
export interface UiStanding {
  reputation?: Record<string, number>;
  milestones?: Array<{ id: string; name: string; milestones: Array<{ id: string; name: string; description: string; completed: boolean; unlocked: boolean }> }>;
  licenses?: Array<{ id: string; name: string; status: string; daysLeft: number | null; feeCents?: number; waitingDays?: number; inspectionDays?: number; phaseHoursLeft?: number | null; renewalDays?: number }>;
  education?: { active: { id: string; defId: string; name: string; daysLeft: number; hoursLeft?:number } | null; programs: Array<{ id: string; name: string; durationDays: number; costCents?: number; cooldownDays?: number; lastAttendance?: number | null; blockedReason?: string | null; expectedAttendance?:number; expectedIncomeCents?:number; ticketCents?:number; expectedReputation?:{track:string;delta:number}; requiresSpecies?:string|null }> };
  grants?: Array<{ id: string; name: string; partner: string; status: 'offered' | 'active' | 'completed' | 'failed' | 'breached'; hoursLeft?: number|null; overdue?:boolean; deliverableProgress?: import('./story-projection').UiJournalRequirement; conditionProgress?: import('./story-projection').UiJournalRequirement|null; graceHoursLeft?:number|null; acceptBlockedReason?: string | null; eligibilityText?: string; researchGateText?: string; conditionsText?: string; watched?: boolean; partnerBlocked?: boolean; daysLeft: number | null; payoutCents?: number; deadlineDays?: number; deliverableText?: string }>;
  speciesFee?: { annualPerSpeciesCents: number; distinctSpecies: number };
  jurisdictions?: Array<{ id: string; name: string; quarantineDays: number; bannedCount: number }>;
  importHolds?: string[]; exportCounts?: Record<string, number>;
  tours?: { vehicleKinds?: Array<{ kind: string; costCents: number }>; vehicles: Array<{ id: string; kind: string }>; routes: Array<{ id: string; name: string; stops: number; stopIds?: string[]; reachable?: boolean }>; rating: number; runs: number };
  lodging?: { kinds?: Array<{ kind: string; costCents: number; capacity: number }>; unitList?: Array<{ id: string; kind: string; parcelId: string | null; attractionScore?: number }>; units: number; priceCentsPerNight: number; review: number };
  eventCatalog?: Array<{ id: string; name: string; guests: number; durationDays: number; incomeCents: number; baseIncomeCents?: number; staffRequired?: number; cleanupDays?: number;refusal?:string|null;staffIds?:string[];freeStaff?:number;capacity?:number;bookedGuests?:number;moraleHit?:number;completionRep?:number }>;
  events?: Array<{ id: string; defId: string; guests: number; daysLeft: number; hoursLeft?:number;cleanupHoursLeft?:number;awaitingDailyCompletion?:boolean;cleaning: boolean }>;
  regions?: Array<{ id: string; name: string; unlocked: boolean; state?: 'locked' | 'available' | 'open'; missing?: RegionMissing[] }>;
  treatCostCents?: number;
  vaccineCostCents?: number;
}
/** Business slices from ui.mgmt (specials lane, #: partners, meat, specials, genetics, rewilding). All optional. */
/** Staff-depth (P605) projection requested from Mgmt-Sim: not provided by the sim yet. */
export interface UiStaffDepth { enabled: boolean; careers: Array<{ staffId: string; role?: string; level: number; xp: number; nextLevelXp: number | null; senior?: boolean; mentorEligible?: boolean }>; mentoring: Array<{ mentorId: string; menteeId: string }>; training?: { costCentsById: Record<string, number>; skillGain: number } }
export interface UiOutlook {
  season?: { current: string; next: { id: string; daysUntil: number } | null };
}
/** ui.finance slice (finance system, PR #348). Field names as the sim code emits them. */
export interface UiFinanceDepth {
  creditRating?: number; nextPaymentDueDay?: number; installmentDueCents?: number; autoDebit?: boolean; loanBalanceCents?: number; taxBp?: number;
  creditLine?: { limitCents: number; drawnCents: number; availableCents: number; apr: number; dailyInterestCents: number };
  refinanceQuote?: { feeCents: number; currentApr: number; newApr: number; interestSavedCents: number; netBenefitCents: number } | null;
  payoffQuote?: { feeCents: number; principalCents: number; interestAvoidedCents: number; netSavedCents: number } | null;
  /** Mgmt-Sim #357: tiered insurance. Deposits and other tiers' premiums are not projected. */
  insurance?: { held: boolean; tier: number; tierName: string; dailyPremiumCents: number; covers: string[]; payoutPerAnimalLossCents: number; payoutPerDisasterCents: number; tiers?: Array<{ tier: number; name: string; depositCents: number; dailyPremiumCents: number; changeCostCents?: number }> };
}
export interface UiFacilityDef { id: string; costCents: number; effectText: string }
export interface UiTaxes { easements: string[]; easementDetails?: Array<{ parcelId: string; liftFeeCents: number }>; unpaidCents: number; totalPaidCents: number; lastBillCents: number; assessments: number; projected: { propertyCents: number; livestockCents: number; totalCents: number } }
/** ui.mgmt.vegetation (Mgmt-Sim #380): per-enclosure forage. Tile grid is ignored here. */
export interface UiVegetation { enclosureId: string; areaHa: number; biomassKg: number; maxKg: number; bareSoil: boolean; overgrazed: boolean; recoveryDays: number }
/** ui.mgmt.restoration (Mgmt-Sim #384): restored land, credit batches, price, audit odds. micro = 1e-6 credit. */
export interface UiRestoration { options?: Array<{parcelId:string;areaHa:number;costCents:number;blockedReason:string|null}>; terms?: {fullYieldCreditsPerHaYear:number;rampDays:number;batchDays:number}; restored: Array<{ parcelId: string; areaHa: number; ageDays: number; yieldBp: number; dailyMicro: number }>; creditMicro: number; batches: Array<{ id: string; vintageDay: number; micro: number; voided: boolean }>; totalSoldMicro: number; price: { centsPerCredit: number; floorCents: number; ceilCents: number }; audit: { chanceOneIn: number; passBp: number } }
/** ui.mgmt.zoo (Mgmt-Sim #368): gate price, visitors, satisfaction.
 *  Depth fields (#376 guest conflicts, #383 guest depth) are optional so
 *  older workers still decode. */
/** ui.zoo.segments (PROPOSED - sim side routed via Main, not yet projected): who the
 *  gate crowd is. shareBp of daily visitors; priceToleranceBp = how far above the
 *  reference ticket the segment accepts before its demand falls; appealWeightsBp
 *  per species id (what draws the segment). PLACEHOLDER design values. */
export interface UiZooSegment { id: string; label: string; shareBp: number; priceToleranceBp: number; appealWeightsBp?: Record<string, number>; }
export interface UiZoo { ticketCents: number; referenceTicketCents: number; visitorsToday: number; satisfaction: number; satisfactionTarget: number; attractiveness: number; projectedVisitors: number; lifetimeVisitors: number; lifetimeTicketCents: number; topAppeal: Array<{ animalId: string; species: string; appeal: number }>; segments?: UiZooSegment[];
  gateBonusBp?: number; diseaseRiskBpPerDay?: number; crowdingExcess?: number;
  exhibits?: Array<{ enclosureId: string; rating: number; animals: number; capacity: number; crowded: boolean }> }
/** Ranch buyer wants, exact eligible quotes/refusals, and unpaid shipments. */
export interface UiStockBoard {
  wants: Array<{ id: string; buyer: string; species: string; sex: 'M' | 'F' | null; minAgeDays: number; maxAgeDays: number; minHealth: number; maxInbreedingBp: number; premiumBp: number; feeBp: number; shipDays: number; expiresTick: number; eligible: string[];
    quotes?: Array<{ animalId: string; grossCents: number; netCents: number; dealerCents: number }>; refusals?: Array<{ animalId: string; reason: string }> }>;
  shipments: Array<{ id: string; wantId: string; buyer: string; animalId: string; species: string; dueTick: number; amountCents: number }>;
  nextBoardTick: number;
}
/** ui.mgmt.morphGoals (Mgmt-Sim #418): Content-Data's goal catalog with completion. Points and hint text are PLACEHOLDER copy. */
export interface UiMorphGoal { id: string; species: string; morph: string; tier: string; mode: string; points: number; hint: string; completed: boolean; rewardCashCents?: number; rewardBreedersRep?: number; }
/** ui.mgmt.hybridGoals (Mgmt-Sim #429): one goal per sourced cross. Points, rewards and names are PLACEHOLDER copy. */
export interface UiHybridGoal { id: string; cross: string; name: string; points: number; rewardCashCents: number; hint: string; completed: boolean; }
export interface UiBusiness extends UiOutlook {
  workforce?:ReturnType<typeof import('../sim/systems/workforceBoard').workforceBoard>;
  guestMap?: import('../sim/guest/runtimeState').GuestMapState & {venueStatus:Array<{id:string;allowNew:boolean;setupPending:boolean}>;ownedParcels:Array<{id:string;name:string;hectares:number}>};
  morphGoals?: UiMorphGoal[]; hybridGoals?: UiHybridGoal[];
  stock?: UiStockBoard;
  zoo?: UiZoo;
  restoration?: UiRestoration;
  vegetation?: UiVegetation[];
  taxes?: UiTaxes;
  auctionTerms?:{durationDays:number;feeBp:number};
  auction?: Array<{ id: string; species: string; sex: string; daysLeft: number;animalId?:string;reserveCents?:number }>;
  crateIntakePending?: Array<{ crateId: string; species: string; sex: string }>;
  challenges?: Array<{ id: string; defId: string; kind: string; name: string; daysLeft: number;hoursLeft?:number; progress?: { current: number; target: number } | null }>;
  staffDepth?: UiStaffDepth;
  fences?: Array<{ id: string; integrityBp: number }>;
  timeshare?: { soldUnitIds?: string[]; count?: number; units?: Array<{ unitId: string; kind: string; parcelId: string | null; sold: boolean; upfrontCents: number; feeCentsYear: number;refusal?:string|null;upkeepCentsDay?:number;daysPerYear?:number }> };
  camping?: { minBuildSafety: number; parcels: Array<{ parcelId: string; minSafety: number; allowed: boolean }> };
  partners?: { active: Array<{ id: string; defId: string; name: string; stream: string; shareBp: number; paidCents: number; daysLeft: number; hoursLeft?: number }>; completed: string[];
    catalog: Array<{ id: string; name: string; stream: string; shareBp: number; upfrontCents: number; termDays: number; minReputation?: {track:string;value:number}; refusal?:string|null; completionReputation?:{track:string;delta:number}|null }> };
  meat?: { plantCostCents?:number;plantRefusal?:string|null;roster?:Array<{animalId:string;kg:number|null;blockedReason:string|null}>;terms?:{cutsShelfDays:number;jerkyDays:number;jerkyShelfDays:number;jerkyYieldBp:number;restaurantMaxKgPerDay:number};plantBuilt: boolean; salesPausedDaysLeft: number; stock: Array<{ id: string; species: string; kind: string; kg: number; ready: boolean; expired?:boolean; daysLeft: number }>; soldCents: number; soldKg: number; channels?: Array<{ id: string; priceBp: number }> };
  specials?: { active: Array<{ id: string; defId: string; name: string; stream: string; discountBp: number; daysLeft: number; hoursLeft?:number }>;
    catalog: Array<{ id: string; name: string; stream: string; discountBp: number; durationDays: number; marketingCostCents: number; cooldownDays: number; refusal?:string|null;cooldownHours?:number;completed?:number;producerWired?:boolean;demandLiftBp?:number;demandGuests?:number }> };
  genetics?: { orders?:Array<{species:string;refusal:string|null}>;sendCooldownDays?:number;applications?:Array<{sampleId:string;damId:string;refusal:string|null;gestationDays:number|null}>;catalog?:Array<{id:string;species:string;name:string;institution:string;kind:string;country:string|null;pedigree:string}>;conceptions?:Array<{id:string;damId:string;sireId:string;donorId:string|null;conceivedTick:number;offspringIds:string[];damCoat:string;sireCoat:string|null}>; donors?: Array<{ id: string; refusal: string | null }>; held: number; inTransit: number; sentCount: number; earnedCents: number; orderCostCents: number; sendPriceCents: number; transportDays: number;
    samples?: Array<{ id: string; species: string; sex?: string; source?: string; availableTick: number; available?: boolean }> };
  finance?: { missedPayments: number; dailyServiceDueCents: number; aprPercentNow: number };
  rewilding?: { roster?:ReturnType<typeof import('../sim/systems/rewilding').rewildingRoster>; totalSent: number; totalBonusCents: number; bySpecies: Array<{ species: string; count: number }>; minConservationists: number; minGroup: number; eligible: boolean };
}
/** Sim decision events (P614): the player answers with decision.choose {decisionId, optionId}. */
/** Option effect keys (Mgmt-Sim #382). Optional: the slice does not project them yet. Amounts are PLACEHOLDERS. */
export interface UiDecisionEffects {
  cash_cents?: number; morale_delta?: number; stress_delta?: number; forage_restore_bp?: number; sell_oldest_animal_bp?: number; recover_escapees?: boolean;
  reputation_delta?: { track: string; delta: number }; breeding_bonus_window?: { conception_bonus_bp: number; days: number }; add_animal?: { species: string; count: number };
  research_discount_bp?: number; free_research?: { id: string }; demand_lift?: { stream: 'lodging' | 'tour'; bp: number; days: number };
}
export interface UiDecision { id: string; defId: string; prompt: string; options: Array<{ id: string; label: string; effects?: UiDecisionEffects;refusal?:string|null;sale?:{animalId:string;name:string;species:string;sex:string;payoutCents:number}|null }>; daysLeft: number }
/**
 * Breeding detail (requested from Sim-Core, all optional): pedigree parents per animal id, and per candidate
 * pair the inbreeding coefficient (bp, 10000 = 100%) and offspring coat odds (bp).
 */
export interface UiBreeding {
  /** Bounded durable log, newest last. Absent on older workers. */
  pairOutcomes?: UiPairOutcome[];
  pedigree?: Record<string, { sire: string | null; dam: string | null }>;
  pairs?: Array<{ refusalByStrategy?: Partial<Record<'balanced' | 'line' | 'outcross', string | null>>;coatEvidenceComplete?:boolean;sireId: string; damId: string; inbreedingBp: number; sireGenotypeKnown?: boolean; damGenotypeKnown?: boolean; sireCarriers?: string[]; damCarriers?: string[]; oddsEstimate?: boolean; odds?: Array<{ coat: string; bp: number }>; oddsRange?: Array<{ coat: string; lowBp: number; highBp: number }>; litter?: { min: number; max: number } | null; sexRatioBp?: number; inbredOffspringHealthPenalty?: number; crossPen?: boolean; hybridCrossId?: string | null; hybridOffspringName?: string | null; hybridF1Fertility?: string | null; crossConceptionBp?: number | null; fertilityBlocked?: string | null; fertilityConceptionBp?: number | null;
    /** Plan §13: effective conception chance (mirrors the real roll) and the
     *  inbreeding multipliers at this pair's F (10000 = no effect). */
    conceptionChanceBp?: number; inbredFertilityBp?: number; inbredSurvivalBp?: number;
    /** Quantitative-trait outlook per trait of the dam's species (0..100). */
    traitOutlook?: Array<{ traitId: string; label: string; low: number; mean: number; high: number }> }>;
}
/** Normalised on decode: the sim sends dueDay (Sim-Core #332) or older dueTick; both are always filled here. */
export interface UiPregnancy { damId: string; sireId: string; dueTick: number; dueDay: number }
export interface UiObserveReport { species: string; siteKind: 'field' | 'enclosure'; notes: string[]; researchPoints: number; spottingBonusBp: number; completedTick: number }
export interface UiObserve {
  session: { species: string; site: { kind: 'field' } | { kind: 'enclosure'; enclosureId: string }; ticksToComplete: number } | null;
  recentReports: UiObserveReport[]; bySpecies: Record<string, { sessions: number; researchPoints: number }>;
}
export interface UiExpedition {
  biomePresence?: Array<{id:string;eligibleAnimals:number}>;
  contractCrews?: Array<{ id: string; name: string; costCents: number; bonusDraws: number; searchTicksDelta: number }>;
  /** P303 (#189): seven stages. */
  status: 'idle' | 'travel' | 'camp' | 'scout' | 'track' | 'capture' | 'crate' | 'return'; targetBiome: string | null; hiredCrewId: string | null; ticksRemainingInPhase: number;
  mode: 'auto' | 'manual'; awaitingAdvance: boolean;
  /** Biomes a field map can generate (sim FIELD_BIOMES); absent on older sims. */
  biomes?: string[];
  /** Tick the current stage began (sim #189); absent on older sims. */
  phaseStartTick?: number;
  lastReport: { biome: string; sightings: Array<{ species: string; count: number }>; returnedTick: number; recalled: boolean; failedStage?: 'track' | 'capture' | null; captured?: { species: string; count: number; animalId?: string; crateId?: string } | null } | null;
}
/** Sticky completion of the last field minigame session (sim ui.field.lastMinigameOutcome, #189). */
export interface UiMinigameOutcome { kind: 'tracking' | 'darting' | 'capture'; targetId: string; result: 'success' | 'fail'; tick: number }
/** Field capture slice (sim ui.field, docs/COMMANDS.md field.*): targets, downed animals, field traps, crate journeys. */
export interface UiFieldCapture {
  zoomBp?:number;maxZoomBp?:number;binocularTier?:number;
  lastTrackingOutcome?:{animalId:string;species:string;tick:number;outcome:'found'|'trail_lost'|'ended';reason:'revealed'|'noise'|'range'|'unavailable'|'captured'}|null;
  tracked?: { animalId:string; freshness:number; points:Array<{xM:number;zM:number;ageTicks:number}> } | null;
  trackProgress?:number; windDir?:number;
  world?: UiFieldWorld;
  spotMarkers: Array<{ animalId: string; distanceM: number; tag: 'near' | 'mid' | 'far'; species?: string; displayName?: string }>;
  /** All live wild animals (sim #210), for naming downed animals and trap catches. */
  wildAnimals?: Array<{ animalId: string; species: string; displayName: string }>;
  sedatedAnimalIds: string[]; heldAnimalIds: string[];
  lastDart: { animalId: string; hit: boolean; tick: number; dartType: 'light' | 'standard' | 'heavy' } | null;
  lastNet: { animalId: string; hit: boolean; tick: number } | null;
  fieldTraps: Array<{ id: string; typeId: string; state: 'armed' | 'sprung_empty' | 'holding'; holdingAnimalId: string | null }>;
  inTransit: Array<{ crateId: string; species: string; ticksToArrival: number; mode: string; legsDone: number; legsTotal: number }>;
}
export interface UiMinigame { kind: 'tracking' | 'darting' | 'capture'; targetId: string; progress: number; target: number }
export interface UiPasture { forageKg: Array<number | null>; droughtActive: boolean }
export interface UiHuntReport {
  bookingId: string; guestName: string; kind: 'management' | 'trophy' | 'photo_tour'; day: number; outingQualityBp: number;
  harvested: { source: 'wild' | 'herd'; species: string; sex: 'M' | 'F' | null; animalId: string | null; trophyQualityBp: number } | null;
  satisfaction: number; feeCents: number;
}
export interface UiHuntBooking { bookingId: string; guestName: string; kind: 'management' | 'trophy' | 'photo_tour'; arriveDay: number; nights: number; priceCents: number; status: string; outingStartedTick?: number; delegateStaffId?: string | null }
export interface UiPoaching { chanceBp: number; rangersOnPatrol: number; incidentsTotal: number; lastIncident: { day: number; animalId: string | null; species: string | null; injured: boolean } | null }
/** The real sim sends meatKg as {whole, prime, stew}; the stub sends a number. Rendering the object crashed the Lodge panel. */
function normHunts(raw: unknown): UiHunts | undefined {
  if (raw === undefined || raw === null) { return undefined; }
  const h = raw as Omit<UiHunts, 'meatKg'> & { meatKg: number | { whole?: number; prime?: number; stew?: number } };
  const m = h.meatKg;
  const total = typeof m === 'number' ? m : (m.whole ?? 0) + (m.prime ?? 0) + (m.stew ?? 0);
  return { ...h, meatKg: total };
}
export interface UiHunts {
  actualOutingReports?: import('../sim/systems/booking').OutingReport[];
  missingOutingReports?: { bookingId: string; guestName: string }[];
  bookings: UiHuntBooking[]; reports: UiHuntReport[]; lodgeRating: number; meatKg: number; jerkyKg: number; curingKg: number; rateBp: number;
  special: { discountBp: number; untilDay: number } | null; cullMarks: string[];
  outing?: { bookingId: string; guideStaffId: string | null; returnTick: number } | null;
}
export interface UiDashboardConflict { id: string; severity: 'critical' | 'warning' | 'info'; message: string }
/** P415/P909 dashboard projection (Sim-Core ui.dashboard; null until it lands): status counts + derived conflicts. */
export interface UiDashboard {
  day: number; cash: number; seasonId: string; seasonHuntable: boolean;
  animals: { total: number; ill: number; stressed: number; weak: number };
  staff: { total: number; deployed: number };
  enclosures: number; parcelsOwned: number; activeBookings: number;
  reputation: Record<string, number>;
  conflicts: UiDashboardConflict[];
}
/** P414 neighbors projection (Sim-Core ui.neighbors; null until it lands). */
export interface UiNeighbors { relationship: number }

/** P215 deep studbook projection (Sim-Core ui.studbook; null until it lands). */
export interface UiStudbookSpecies {
  species: string; herdMeanKinshipBp: number;
  founders: Array<{ founderId: string; descendants: number }>;
  recommended: { sireId: string; damId: string; kinshipBp: number } | null;
}
export interface UiStudbook { enabled: boolean; species?: UiStudbookSpecies[] }
import type { UiStory, UiLifeLog } from './story-projection';

/** P510 price history projection (Sim-Core ui.price-history; null until it lands). */
export interface UiPriceHistory { days: number; series: Record<string, Array<{ day: number; priceCents: number }>> }

/** Request-correlated outcomes. requestId is the worker ack command id returned by sendTracked. */
export interface UiPairOutcome { requestId: string | null; tick: number; day: number; animalId?: string; sireId: string | null; damId: string | null; outcome: 'moved' | 'conceived' | 'failed' | 'refused'; reason?: string }
export interface UiDealerOutcome { requestId: string | null; tick: number; day: number; offerId: string; enclosureId: string; outcome: 'purchased' | 'refused'; reason?: string; animalId?: string; priceCents?: number }
export interface UiAlertEvent { day: number; type: string; refId: string | null; message: string }
export interface UiTreatmentReceipt {requestId:string;tick:number;maxTotalCents:number;chargedCents:number;treated:string[];failed:{animalId:string;reason:string}[]}
export interface UiMoveOutcome { requestId: string | null; tick: number; day: number; enclosureId: string; moved: string[]; failed: { animalId: string; reason: string }[] }
export interface UiState {
  ranchOperations?:ReturnType<typeof import('../sim/systems/ranchOperations').projectRanchOperations>;
  alertPolicy?: {enabled:Record<import('../sim/systems/alertPolicy').PauseEvent,boolean>;lastPause:{event:string;tick:number}|null;mutedTypes?:string[];readThroughId?:number;unreadCount?:number}|null;
  fieldJournal?: {entries:Array<{id:number;tick:number;category:string;message:string}>;discoveries:Array<{species:string;coat:string|null;tick:number;source:string;name:string;biomes:string[];diet:string|null;lifespanDays:number|null}>}|null;
  tick: number; speed: Speed; cashCents: number;
  animals: UiAnimal[]; staffRoles: UiStaffRole[]; staffHireCosts?: UiStaffHireCost[];
  staffActive: number; staffCapacity: number; deployMax: number; overworked: boolean;
  alerts: string[];
  /** One-shot offline catch-up summary shown as a dismissible banner after boot restore (worker-reported; UI-only, never re-derived). */
  awayNotice: string | null;
  /** Persistent recovery safety state, UI-only. Never dismiss its warning. */
  savingDisabled?: boolean;
  /** Today's alerts with type and refId (pair outcomes: refId is `${sireId}:${damId}`; an animalId-only refusal carries the animalId). Today only: older ones drop at day rollover. */
  alertEvents: UiAlertEvent[];
  /** dealer.buy outcomes keyed by requestId (bounded, durable). Empty on older workers. */
  dealerOutcomes: UiDealerOutcome[];
  /** herd.move outcomes keyed by requestId (bounded, durable). Empty on older workers. */
  herdSales?: import('../sim/systems/herdSales').HerdSalesState;
  treatmentReceipts?: UiTreatmentReceipt[];
  moveOutcomes: UiMoveOutcome[];
  field: UiFieldState;
  fieldWorld?: UiFieldWorld | null;
  // Economy-loop projections (Sim-Core ui.hud, docs/COMMANDS.md)
  licence: boolean; insurance: boolean; loan: UiLoan | null;
  parcels: UiParcel[]; enclosures: UiEnclosure[];
  fenceLayouts: Record<string, UiFenceLayout>;
  /** P104: enclosure id -> claimed grid cells (for demolition consequence preview). */ penCells: Record<string, number[]>;
  dealerOffers: UiDealerOffer[]; dealerNextRefreshTick: number;
  staff: UiStaffMember[]; traps: UiTrap[];
  enclosureTypes: Record<string, UiEnclosureType>; trapTypes: string[];
  startModes: string[];
  /** Per-mode opening cash in cents, from the live balance catalog. */
  startModeCash: Record<string, number>;
  /** Monthly P&L (#29), contract board (#31), research (#30). */
  ledgerMonthly: UiMonthBucket[];
  pregnancies: UiPregnancy[];
  decisions: UiDecision[];
  business: UiBusiness | null;
  breeding: UiBreeding | null;
  realism: number | null;
  standing: UiStanding | null;
  identity: UiIdentity | null;
  loanTerms: { takeAprPercent: number; maxPrincipalCents: number } | null;
  hunts: UiHunts | null;
  poaching: UiPoaching | null;
  minigameOutcome: UiMinigameOutcome | null;
  fieldCapture: UiFieldCapture | null;
  observe: UiObserve | null; expedition: UiExpedition | null;
  minigames: UiMinigame[];
  pasture: UiPasture | null;
  contractOffers: UiContractOffer[]; activeContracts: UiContractOffer[];
  research: UiResearch;
  researchTree: UiResearchTree | null;
  forecast: UiForecast | null;
  advisors: UiAdvisors | null;
  facilitiesBuilt: string[] | null;
  /** ui.facilities.staffBeds (PROPOSED - sim side routed via Main): total staff bed
   *  capacity (office base + staff_housing). null = older sim, no gate shown. */
  staffBeds: number | null;
  facilityCatalog: UiFacilityDef[] | null;
  financeDepth: UiFinanceDepth | null;
  studbook: UiStudbook | null;
  priceHistory: UiPriceHistory | null;
  dashboard: UiDashboard | null;
  neighbors: UiNeighbors | null;
  /** Story lane (P707+9.5): campaign reserves + celebrated moments. null = older sim. */
  story: UiStory | null;
  /** Story lane (P213): per-animal life-event log. null = older sim. */
  lifelog: UiLifeLog | null;
}

/** Empty economy projections shared by stub init + live init. */
const EMPTY_ECONOMY = {
  licence: false, insurance: false, loan: null,
  parcels: [] as UiParcel[], enclosures: [] as UiEnclosure[],
  fenceLayouts: {} as Record<string, UiFenceLayout>,
  dealerOffers: [] as UiDealerOffer[], dealerNextRefreshTick: 0,
  staff: [] as UiStaffMember[], traps: [] as UiTrap[],
  enclosureTypes: {} as Record<string, UiEnclosureType>, trapTypes: [] as string[],
  startModes: ['easy', 'standard', 'hard'],
  startModeCash: {},
  ledgerMonthly: [] as UiMonthBucket[],
  pregnancies: [] as UiPregnancy[],
  decisions: [] as UiDecision[],
  business: null as UiBusiness | null,
  breeding: null as UiBreeding | null,
  realism: null as number | null,
  standing: null as UiStanding | null,
  identity: null as UiIdentity | null,
  loanTerms: null as { takeAprPercent: number; maxPrincipalCents: number } | null,
  hunts: null as UiHunts | null,
  poaching: null as UiPoaching | null,
  minigameOutcome: null as UiMinigameOutcome | null,
  fieldCapture: null as UiFieldCapture | null,
  observe: null as UiObserve | null, expedition: null as UiExpedition | null,
  minigames: [] as UiMinigame[],
  pasture: null as UiPasture | null,
  contractOffers: [] as UiContractOffer[], activeContracts: [] as UiContractOffer[],
  research: { active: null, completed: [] as string[], catalog: [] as UiStudy[] } as UiResearch,
  researchTree: null as UiResearchTree | null,
  forecast: null as UiForecast | null,
  advisors: null as UiState['advisors'],
  facilitiesBuilt: null as string[] | null, staffBeds: null as number | null,
  facilityCatalog: null as UiFacilityDef[] | null,
  financeDepth: null as UiFinanceDepth | null,
  studbook: null as UiStudbook | null,
  priceHistory: null as UiPriceHistory | null,
  alertPolicy: null,
  story: null as UiStory | null,
  lifelog: null as UiLifeLog | null,
  dashboard: null as UiDashboard | null,
  neighbors: null as UiNeighbors | null,
};

export interface SimBridge {
  getState(): UiState;
  exportCurrentSave?(): Promise<string>;
  /** P129 save slots: per-slot summaries (null entries = empty, corrupt flagged). */
  listSaveSlots?(): SlotInfo[];
  exportPreviousGame?(): string | null;
  /** Save the live game into a slot (previous content rotates to its one-deep backup). */
  saveToSlot?(slot: number): Promise<void>;
  /** Load a slot into the live game; live progress is first protected into the autosave slot. */
  loadFromSlot?(slot: number): Promise<void>;
  /** Promote a slot's one-deep backup and load it (P129 damaged-save fallback). */
  restoreSlotBackup?(slot: number): Promise<void>;
  /** Import an exported save file: the worker validates atomically (a bad file changes nothing), the live game is protected first, and the imported game is persisted. */
  importSaveFile?(text: string): Promise<{ tick: number }>;
  /** Fractional tick for frame-rate rendering (interpolates between polls). */
  tickFloat(): number;
  subscribe(fn: (s: UiState) => void): () => void;
  send(type: string, payload: unknown): void;
  /** Like send, but resolves with the worker's ack command id (the requestId used by outcome logs), or null when the build cannot track (preview). Rejects when the boundary refuses the command. */
  sendTracked(type: string, payload: unknown): Promise<string | null>;
  /** Dismiss the one-shot offline catch-up banner. */
  dismissAwayNotice(): void;
  /** true once the live worker is driving tick/speed. */
  isLive(): boolean;
  monetization: MonetizationHooks;
}

const START_CASH_CENTS = 12_578_000; // shows as 125,780
const DAY_HOURS = 24;
const TICKS_PER_SECOND_1X = 1 / 90; // frozen contract: 1x = one game hour per 90 real seconds (Matthew 2026-10-22 pacing)
const DEFAULT_SEED = 20261021;

/** Lane 4's command registry uses 'ranch.path'; keep UI wording stable here. */
const COMMAND_ALIASES: Record<string, string> = {
  'ranch.setPath': 'ranch.path',
};

export function createStubBridge(): SimBridge {
  // QA harness: ?tick=N pins the clock (and world lighting) to tick N so
  // captures can target mockup lighting (e.g. 270 = Oct 12, 6 AM dawn).
  const pinnedTick = (() => {
    if (typeof location === 'undefined') return null;
    const raw = new URLSearchParams(location.search).get('tick');
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  })();
  let state: UiState = {
    penCells: {},
    tick: pinnedTick ?? 11 * 24 + 18, // Oct 12, 6 PM like the mockup HUD
    speed: pinnedTick !== null ? 0 : 1, // pinned captures hold the lighting still
    cashCents: START_CASH_CENTS,
    animals: [
      { id: 'a1', name: 'Kavari', species: 'Chital', sex: 'M', coat: 'Spotted', temperament: 'Calm', traits: ['Hardy', 'Gentle', 'Showy'], rare: true, health: 96, stress: 8, enclosure: 'East Paddock', biome: 'Savanna', rarity: 'rare', ill: false,
        knowledge: { healthKnown: true, health: 96, healthConfidenceBp: 10_000, qualityKnown: true, qualityConfidenceBp: 10_000, qualityComponents: { trophy: null, morphMultiplierBp: 10_000, health: 96, outbreeding: 100, inbreedingBp: 0, complete: false }, geneticsKnown: true, costs: { inspectCents: 50_00, vetCheckCents: 100_00, dnaTestCents: 250_00 } } },
      { id: 'a2', name: 'Amara', species: 'Chital', sex: 'F', coat: 'Spotted', temperament: 'Wary', traits: ['Hardy'], rare: false, health: 91, stress: 21, enclosure: 'East Paddock', biome: 'Savanna', rarity: 'common', ill: false },
      { id: 'a3', name: 'Jabali', species: 'Blackbuck', sex: 'M', coat: 'Dark saddle', temperament: 'Spirited', traits: ['Fast'], rare: false, health: 88, stress: 30, enclosure: 'North Run', biome: 'Scrub', rarity: 'uncommon', ill: true,
        knowledge: { healthKnown: false, health: 90, healthConfidenceBp: 5000, qualityKnown: false, qualityConfidenceBp: 5000, geneticsKnown: false, costs: { inspectCents: 50_00, vetCheckCents: 100_00, dnaTestCents: 250_00 } } },
      { id: 'a4', name: 'Zuri', species: 'Plains Zebra', sex: 'F', coat: 'Striped', temperament: 'Bold', traits: ['Sure-footed', 'Social'], rare: false, health: 93, stress: 14, enclosure: 'North Run', biome: 'Savanna', rarity: 'uncommon', ill: false },
      { id: 'a5', name: 'Onyeka', species: 'Ostrich', sex: 'F', coat: 'Grey-brown', temperament: 'Skittish', traits: [], rare: false, health: 84, stress: 41, enclosure: 'West Yard', biome: 'Grassland', rarity: 'common', ill: false },
    ],
    staffRoles: [
      { role: 'Rangers', duty: 'Patrol - Trail Monitor', count: 12, portrait: 'ranger' },
      { role: 'Veterinarians', duty: 'Animal Care - Treatment', count: 4, portrait: 'veterinarian' },
      { role: 'Scouts', duty: 'Survey - Habitat Study', count: 7, portrait: 'scout' },
    ],
    staffActive: 3, staffCapacity: 12, deployMax: 8, overworked: true,
    alerts: ['Fence wear: East Paddock'],
    awayNotice: null,
    alertEvents: [],
    dealerOutcomes: [],
    moveOutcomes: [],
    field: {
      goal: 'Find fresh chital tracks',
      trapsRemaining: 4,
      stealth: { noise: 0.4, visibility: 0.55 },
    },
    ...EMPTY_ECONOMY,
    licence: true,
    loan: { principal: 500_00, aprPercent: 6 },
    parcels: [
      { id: 'p1', hectares: 12, landClass: 'Savanna', distanceKm: 0.4, price: 0, owned: true },
      { id: 'p2', hectares: 20, landClass: 'Scrub', distanceKm: 1.1, price: 450_000_00, owned: false },
      { id: 'p3', hectares: 8, landClass: 'Wetland', distanceKm: 2.3, price: 610_000_00, owned: false },
    ],
    enclosures: [
      { id: 'e1', parcelId: 'p1', type: 'paddock', areaHa: 4, fencePerimeterM: 800, waterPoints: 1, animalCount: 5 },
    ],
    dealerOffers: [
      { id: 'o1', species: 'axis deer', sex: 'F', ageDays: 620, health: 92, priceCents: 3_200_00 },
      { id: 'o2', species: 'blackbuck', sex: 'M', ageDays: 840, health: 88, priceCents: 4_750_00 },
      { id: 'o3', species: 'plains zebra', sex: 'F', ageDays: 1100, health: 95, priceCents: 12_900_00 },
    ],
    staff: [
      { id: 's1', role: 'ranger', assignedTo: 'e1', fatigue: 62, skill: 62, morale: 55, wagePerDay: 120_00 },
      { id: 's2', role: 'veterinarian', assignedTo: null, fatigue: 20, skill: 74, morale: 70, manager: true, wagePerDay: 225_00 },
      { id: 's3', role: 'scout', assignedTo: null, fatigue: 35, skill: 50, morale: 68, wagePerDay: 100_00 },
    ],
    traps: [{ id: 't1', parcelId: 'p1', type: 'pitfall', baited: true, ready: true, readyTick: 0 }],
    enclosureTypes: {
      paddock: { base_cost_cents: 0, fence_cost_per_m_cents: 2000, water_point_cost_cents: 150000, density_per_ha: 3, groups: ['hoofstock', 'ratite'] },
      pasture: { base_cost_cents: 50000, fence_cost_per_m_cents: 500, water_point_cost_cents: 150000, density_per_ha: 6, groups: ['hoofstock'] },
    },
    trapTypes: ['pitfall', 'snare', 'dart'],
    pregnancies: [{ damId: 'a2', sireId: 'a1', dueTick: 24 * 20, dueDay: 20 }],
    decisions: [{ id: 'd1', defId: 'neighbor_offer', prompt: 'A neighbouring rancher offers to share fence costs along the east boundary.', options: [{ id: 'accept', label: 'Share the cost', effects: { cash_cents: -150_000, morale_delta: 2, reputation_delta: { track: 'governments', delta: 3 } } }, { id: 'decline', label: 'Decline politely' }], daysLeft: 4 }],
    facilitiesBuilt: ['feed_silo', 'staff_housing'], staffBeds: 6,
    studbook: {
      enabled: true,
      species: [
        { species: 'chital', herdMeanKinshipBp: 1875,
          founders: [{ founderId: 'a1', descendants: 3 }, { founderId: 'a2', descendants: 3 }],
          recommended: { sireId: 'a1', damId: 'a2', kinshipBp: 0 } },
        { species: 'blackbuck', herdMeanKinshipBp: 2600,
          founders: [{ founderId: 'a3', descendants: 1 }],
          recommended: null },
      ],
    },
    priceHistory: {
      days: 90,
      series: {
        axis_deer: Array.from({ length: 45 }, (_, d) => ({ day: d, priceCents: 3_200_00 + Math.round(3_200_00 * 0.08 * Math.sin(d / 6)) + d * 1_00 })),
        blackbuck: Array.from({ length: 45 }, (_, d) => ({ day: d, priceCents: 4_750_00 + Math.round(4_750_00 * 0.11 * Math.cos(d / 9)) - d * 2_00 })),
        plains_zebra: Array.from({ length: 45 }, (_, d) => ({ day: d, priceCents: 12_900_00 + Math.round(12_900_00 * 0.05 * Math.sin(d / 4 + 1)) })),
      },
    },
    financeDepth: { insurance: { held: true, tier: 1, tierName: 'basic', dailyPremiumCents: 500, covers: ['disease'], payoutPerAnimalLossCents: 100_000, payoutPerDisasterCents: 150_000, tiers: [{ tier: 1, name: 'basic', depositCents: 200_000, dailyPremiumCents: 500 }, { tier: 2, name: 'standard', depositCents: 400_000, dailyPremiumCents: 1200 }, { tier: 3, name: 'premium', depositCents: 600_000, dailyPremiumCents: 2500 }] }, creditRating: 64, loanBalanceCents: 50_000, creditLine: { limitCents: 5_000_000, drawnCents: 1_000_000, availableCents: 4_000_000, apr: 14, dailyInterestCents: 384 },
      refinanceQuote: { feeCents: 500, currentApr: 12, newApr: 9, interestSavedCents: 1_500, netBenefitCents: 1_000 }, payoffQuote: { feeCents: 250, principalCents: 50_000, interestAvoidedCents: 6_000, netSavedCents: 5_750 } },
    facilityCatalog: [{ id: 'vet_clinic', costCents: 15_000_000, effectText: 'Ill animals regain +5 health daily' }, { id: 'feed_silo', costCents: 8_000_000, effectText: 'Fed animals stay fed +6 ticks longer each day' }, { id: 'viewing_platform', costCents: 12_000_000, effectText: 'Hunt booking quotes +10%' }],
    business: {
      vegetation: [{ enclosureId: 'e1', areaHa: 6, biomassKg: 3200, maxKg: 6000, bareSoil: false, overgrazed: false, recoveryDays: 0 }, { enclosureId: 'e2', areaHa: 4, biomassKg: 0, maxKg: 4000, bareSoil: true, overgrazed: true, recoveryDays: 18 }],
      hybridGoals: [{ id: 'hgoal_donkey_x_horse', cross: 'donkey_x_horse', name: 'Mule born', points: 3, rewardCashCents: 40_000, hint: 'The cited source says these hybrids are usually sterile, so plan on it being a one-generation animal.', completed: false }, { id: 'hgoal_horse_x_donkey', cross: 'horse_x_donkey', name: 'Hinny born', points: 3, rewardCashCents: 40_000, hint: 'Sterile, so it cannot breed on.', completed: true }],
      morphGoals: [{ id: 'goal_axis_piebald', species: 'axis_deer', morph: 'axis_piebald', tier: 'rare', mode: 'recessive', points: 3, rewardCashCents: 75_000, rewardBreedersRep: 1, hint: 'Both parents must carry the gene. Pair two carriers and expect about one pup in four to show it.', completed: true }, { id: 'goal_axis_albino', species: 'axis_deer', morph: 'axis_albino', tier: 'very_rare', mode: 'recessive', points: 8, rewardCashCents: 200_000, rewardBreedersRep: 1, hint: 'Both parents must carry the gene. Pair two carriers and expect about one pup in four to show it.', completed: false }, { id: 'goal_fallow_menil', species: 'fallow_deer', morph: 'fallow_menil', tier: 'uncommon', mode: 'dominant', points: 2, hint: 'One carrier parent is enough, so about half the fawns show it.', completed: false }],
      restoration: { restored: [{ parcelId: 'p1', areaHa: 12, ageDays: 400, yieldBp: 2200, dailyMicro: 130_000 }], creditMicro: 3_400_000, batches: [{ id: 'cb1', vintageDay: 3, micro: 2_400_000, voided: false }, { id: 'cb2', vintageDay: 33, micro: 1_000_000, voided: false }, { id: 'cb0', vintageDay: 1, micro: 500_000, voided: true }], totalSoldMicro: 1_000_000, price: { centsPerCredit: 640, floorCents: 150, ceilCents: 1200 }, audit: { chanceOneIn: 8, passBp: 8500 } },
      zoo: { ticketCents: 1500, referenceTicketCents: 1500, visitorsToday: 42, satisfaction: 62, satisfactionTarget: 70, attractiveness: 48, projectedVisitors: 45, lifetimeVisitors: 900, lifetimeTicketCents: 1_350_000, topAppeal: [{ animalId: 'a2', species: 'blackbuck', appeal: 71 }, { animalId: 'a1', species: 'chital', appeal: 55 }],
        segments: [
          { id: 'families', label: 'Families', shareBp: 5500, priceToleranceBp: 8000 },
          { id: 'photographers', label: 'Photographers', shareBp: 2500, priceToleranceBp: 12000, appealWeightsBp: { plains_zebra: 13000, sika_deer: 12000 } },
          { id: 'trophy_scouts', label: 'Trophy Scouts', shareBp: 2000, priceToleranceBp: 14000, appealWeightsBp: { scimitar_oryx: 13000, greater_kudu: 12500 } },
        ],
        gateBonusBp: 500, diseaseRiskBpPerDay: 12, crowdingExcess: 2,
        exhibits: [{ enclosureId: 'e1', rating: 68, animals: 5, capacity: 6, crowded: false }, { enclosureId: 'e2', rating: 41, animals: 7, capacity: 6, crowded: true }] },
      stock: { wants: [{ id: 'w1', buyer: 'Sierra Exotics', species: 'blackbuck', sex: 'F', minAgeDays: 300, maxAgeDays: 2000, minHealth: 70, maxInbreedingBp: 1250, premiumBp: 3000, feeBp: 800, shipDays: 4, expiresTick: 24 * 18, eligible: ['a2'], quotes: [{ animalId: 'a2', grossCents: 390_000, netCents: 358_800, dealerCents: 300_000 }], refusals: [{ animalId: 'a1', reason: 'too young' }, { animalId: 'a3', reason: 'wrong sex' }] },
        { id: 'w2', buyer: 'Mesa Verde Game Co.', species: 'chital', sex: null, minAgeDays: 100, maxAgeDays: 1500, minHealth: 85, maxInbreedingBp: 600, premiumBp: 1800, feeBp: 800, shipDays: 6, expiresTick: 24 * 12, eligible: [] }],
        shipments: [{ id: 'ship-1', wantId: 'w0', buyer: 'Broken Horn Ranch', animalId: 'a9', species: 'chital', dueTick: 24 * 3, amountCents: 210_000 }], nextBoardTick: 24 * 5 },
      taxes: { easements: ['p3'], easementDetails: [{ parcelId: 'p3', liftFeeCents: 36_000 }], unpaidCents: 0, totalPaidCents: 420_000, lastBillCents: 210_000, assessments: 2, projected: { propertyCents: 180_000, livestockCents: 30_000, totalCents: 210_000 } },
      staffDepth: { enabled: true, careers: [{ staffId: 's1', role: 'ranger', level: 2, xp: 40, nextLevelXp: 100, senior: false, mentorEligible: false }, { staffId: 's2', role: 'veterinarian', level: 3, xp: 210, nextLevelXp: null, senior: true, mentorEligible: true }, { staffId: 's3', role: 'scout', level: 1, xp: 5, nextLevelXp: 50, senior: false, mentorEligible: false }], mentoring: [{ mentorId: 's2', menteeId: 's1' }], training: { costCentsById: { s1: 31_000, s2: 37_000, s3: 25_000 }, skillGain: 5 } },
      auction: [{ id: 'l1', species: 'blackbuck', sex: 'M', daysLeft: 3 }],
      crateIntakePending: [{ crateId: 'c1', species: 'chital', sex: 'F' }],
      challenges: [{ id: 'ch1', defId: 'breed5', kind: 'breeding', name: 'Raise five fawns this season', daysLeft: 40, progress: { current: 2, target: 5 } }],
      season: { current: 'dry', next: { id: 'rut', daysUntil: 12 } },
      fences: [{ id: 'e1', integrityBp: 6200 }],
      timeshare: { units: [{ unitId: 'u1', kind: 'cabin', parcelId: 'p1', sold: false, upfrontCents: 3_000_000, feeCentsYear: 240_000 }, { unitId: 'u2', kind: 'cabin', parcelId: null, sold: true, upfrontCents: 3_000_000, feeCentsYear: 240_000 }] },
      camping: { minBuildSafety: 40, parcels: [{ parcelId: 'p1', minSafety: 55, allowed: true }, { parcelId: 'p2', minSafety: 20, allowed: false }] },
      finance: { missedPayments: 1, dailyServiceDueCents: 18_000, aprPercentNow: 11 },
      partners: { active: [{ id: 'p1', defId: 'outfitter', name: 'Ridgeline Outfitters', stream: 'hunting', shareBp: 1500, paidCents: 320_000, daysLeft: 120 }], completed: [],
        catalog: [{ id: 'outfitter', name: 'Ridgeline Outfitters', stream: 'hunting', shareBp: 1500, upfrontCents: 2_000_000, termDays: 180 }, { id: 'tour_op', name: 'Savanna Tour Co', stream: 'tours', shareBp: 1000, upfrontCents: 1_200_000, termDays: 120 }] },
      meat: { plantBuilt: true, salesPausedDaysLeft: 0, stock: [{ id: 'm1', species: 'blackbuck', kind: 'fresh', kg: 42, ready: true, daysLeft: 5 }, { id: 'm2', species: 'chital', kind: 'jerky', kg: 11, ready: true, daysLeft: 90 }], soldCents: 640_000, soldKg: 380, channels: [{ id: 'direct', priceBp: 10000 }, { id: 'restaurant', priceBp: 12000 }, { id: 'wholesale', priceBp: 7500 }] },
      specials: { active: [{ id: 's1', defId: 'weekend', name: 'Weekend lodge special', stream: 'lodging', discountBp: 1500, daysLeft: 3 }],
        catalog: [{ id: 'weekend', name: 'Weekend lodge special', stream: 'lodging', discountBp: 1500, durationDays: 4, marketingCostCents: 150_000, cooldownDays: 14 }, { id: 'tour_bundle', name: 'Tour and stay bundle', stream: 'tours', discountBp: 2000, durationDays: 7, marketingCostCents: 300_000, cooldownDays: 30 }] },
      genetics: { held: 1, inTransit: 1, sentCount: 2, earnedCents: 90_000, orderCostCents: 120_000, sendPriceCents: 200_000, transportDays: 7, samples: [{ id: 'g1', species: 'blackbuck', availableTick: 0 }] },
      rewilding: { totalSent: 4, totalBonusCents: 800_000, bySpecies: [{ species: 'blackbuck', count: 4 }], minConservationists: 60, minGroup: 4, eligible: false },
    },
    realism: 1,
    story: { campaign: { reserves: [{ id: 'reserve_raven_creek', scenario: 'first_ranch', region: 'raven_creek', name: 'Raven Creek (First Ranch)', state: 'active' }, { id: 'reserve_cedar_breaks', scenario: 'oryx_warden', region: 'cedar_breaks', name: 'Cedar Breaks (Oryx Warden)', state: 'locked' }, { id: 'reserve_salt_flat', scenario: 'big_country', region: 'salt_flat_country', name: 'Salt Flat Country (Big Country)', state: 'locked' }], complete: false }, moments: [{ id: 'first_animals_arrive', label: 'First animals arrive', firedTick: 24 }, { id: 'first_birth', label: 'First birth', firedTick: null }] },
    lifelog: { events: [{ id: 'll-1', tick: 24, day: 1, type: 'named', message: 'axis_deer a2 was named Kavari', refId: 'a2' }], entries: [{ id: 1, animalId: 'a2', eventRef: 'll-1' }] },
    breeding: { pedigree: { a2: { sire: 'a1', dam: null }, a1: { sire: null, dam: null } }, pairs: [{ sireId: 'a1', damId: 'a2', inbreedingBp: 1250, sireGenotypeKnown: true, damGenotypeKnown: false, sireCarriers: ['piebald'], damCarriers: [], oddsEstimate: true, litter: { min: 1, max: 2 }, sexRatioBp: 5000, inbredOffspringHealthPenalty: 6, oddsRange: [{ coat: 'Dark saddle', lowBp: 3500, highBp: 6000 }, { coat: 'Wild type', lowBp: 3000, highBp: 5500 }, { coat: 'Pale (rare)', lowBp: 0, highBp: 1500 }], odds: [{ coat: 'Dark saddle', bp: 5000 }, { coat: 'Wild type', bp: 4500 }, { coat: 'Pale (rare)', bp: 500 }] }] },
    identity: { paths: [{ id: 'zoo', level: 2 }, { id: 'hunting_ranch', level: 0 }, { id: 'conservation_breeder', level: 1, gate: { track: 'conservationists', need: 40, level: 3 } }, { id: 'rancher', level: 0 }, { id: 'breeder', level: 3 }, { id: 'conservationist', level: 1 }], knownFor: [{ label: 'Careful breeder', score: 74 }, { label: 'Guest-friendly zoo', score: 52 }, { label: 'Rare-species keeper', score: 31 }] },
    standing: {
      regions: [{ id: 'home', name: 'Home range', unlocked: true, state: 'open', missing: [] }, { id: 'savanna_north', name: 'Northern savanna', unlocked: false, state: 'locked', missing: [{ kind: 'parcels', have: 1, need: 3 }, { kind: 'reputation', track: 'hunters', have: 50, need: 60 }, { kind: 'license', licenseId: 'capture' }, { kind: 'fee', have: 12_578_000, need: 15_000_000 }] }],
      reputation: { zoo_public: 58, hunters: 50, conservationists: 63, governments: 47 },
      licenses: [{ id: 'capture', name: 'Capture licence', status: 'active', daysLeft: 210, feeCents: 2_000_00, waitingDays: 3, renewalDays: 365 }, { id: 'import', name: 'Import licence', status: 'inspection', daysLeft: 3 }, { id: 'export', name: 'Export licence', status: 'none', daysLeft: null }, { id: 'breeding', name: 'Breeding licence', status: 'pending', daysLeft: 6 }],
      jurisdictions: [{ id: 'eu', name: 'European Union', quarantineDays: 30, bannedCount: 12 }, { id: 'jp', name: 'Japan', quarantineDays: 14, bannedCount: 5 }], importHolds: ['a3'], exportCounts: { blackbuck: 2 },
      education: { active: null, programs: [{ id: 'school_day', name: 'School field day', durationDays: 3, costCents: 600_00, cooldownDays: 14 }, { id: 'keeper_talks', name: 'Keeper talks', durationDays: 5 }] },
      grants: [{ id: 'g1', name: 'Savanna monitoring study', partner: 'Verdalis Wildlife Trust', status: 'offered', daysLeft: null, payoutCents: 12_000_00, deadlineDays: 60, deliverableText: 'Hold 6 savanna species for 30 days' }],
      milestones: [{ id: 'zoo', name: 'Zoo', milestones: [{ id: 'm1', name: 'First herd', description: 'Keep five animals.', completed: true, unlocked: true }, { id: 'm2', name: 'Open gates', description: 'Reach 50 reputation.', completed: false, unlocked: true }] }],
      speciesFee: { annualPerSpeciesCents: 120_000, distinctSpecies: 4 }, treatCostCents: 15_000,
      tours: { vehicleKinds: [{ kind: 'jeep', costCents: 2_500_000 }], vehicles: [{ id: 'v1', kind: 'jeep' }], routes: [{ id: 'r1', name: 'Dawn loop', stops: 2 }], rating: 72, runs: 5 },
      lodging: { kinds: [{ kind: 'cabin', costCents: 4_000_000, capacity: 4 }, { kind: 'lodge', costCents: 18_000_000, capacity: 16 }], units: 2, unitList: [{ id: 'u1', kind: 'cabin', parcelId: 'p1' }, { id: 'u2', kind: 'cabin', parcelId: null }], priceCentsPerNight: 15_000, review: 68 },
      eventCatalog: [{ id: 'wedding', name: 'Ranch Wedding', guests: 60, durationDays: 1, incomeCents: 500_000 }, { id: 'farm_stay', name: 'Farm-Stay Weekend', guests: 12, durationDays: 2, incomeCents: 120_000 }],
      events: [{ id: 'e1', defId: 'farm_stay', guests: 12, daysLeft: 1, cleaning: false }],
    },
    loanTerms: { takeAprPercent: 12, maxPrincipalCents: 20_000_000 },
    fieldCapture: { spotMarkers: [{ animalId: 'w1', distanceM: 24, tag: 'near', species: 'blackbuck', displayName: 'Blackbuck' }, { animalId: 'w2', distanceM: 58, tag: 'mid', species: 'axis_deer', displayName: 'Axis deer' }], wildAnimals: [{ animalId: 'w3', species: 'nilgai', displayName: 'Nilgai' }, { animalId: 'w4', species: 'chital', displayName: 'Chital' }], sedatedAnimalIds: ['w3'], heldAnimalIds: [], lastDart: { animalId: 'w3', hit: true, tick: 40, dartType: 'standard' }, lastNet: null, fieldTraps: [{ id: 't1', typeId: 'snare', state: 'holding', holdingAnimalId: 'w4' }, { id: 't2', typeId: 'box_trap', state: 'armed', holdingAnimalId: null }], inTransit: [] },
    poaching: { chanceBp: 550, rangersOnPatrol: 1, incidentsTotal: 2, lastIncident: { day: 4, animalId: 'a2', species: 'blackbuck', injured: true } },
    dashboard: { day: 6, cash: 12_578_000, seasonId: 'early_season', seasonHuntable: true, animals: { total: 4, ill: 1, stressed: 0, weak: 0 }, staff: { total: 5, deployed: 3 }, enclosures: 3, parcelsOwned: 2, activeBookings: 2, reputation: { zoo_public: 58, hunters: 50, conservationists: 63, governments: 47 }, conflicts: [{ id: 'illness', severity: 'warning', message: '1 animal ill' }] },
    neighbors: { relationship: 58 },
    hunts: { bookings: [{ bookingId: 'b3', guestName: 'T. Whitfield', kind: 'trophy', arriveDay: 6, nights: 3, priceCents: 4_200_00, status: 'upcoming' }, { bookingId: 'b4', guestName: 'Forestry Dept', kind: 'management', arriveDay: 5, nights: 2, priceCents: 900_00, status: 'active' }], reports: [{ bookingId: 'b1', guestName: 'R. Calloway', kind: 'trophy', day: 3, outingQualityBp: 7200, harvested: { source: 'wild', species: 'blackbuck', sex: 'M', animalId: null, trophyQualityBp: 6100 }, satisfaction: 82, feeCents: 1_800_00 }, { bookingId: 'b2', guestName: 'M. Ortiz', kind: 'management', day: 4, outingQualityBp: 3100, harvested: null, satisfaction: 38, feeCents: 0 }], lodgeRating: 64, meatKg: 90, jerkyKg: 12, curingKg: 30, rateBp: 10000, special: null, cullMarks: ['a2'] },
    observe: { session: null, bySpecies: { chital: { sessions: 1, researchPoints: 2 } }, recentReports: [{ species: 'chital', siteKind: 'field', notes: ['flee style: bound-and-scatter', 'alarm signal: bark', 'top speed: 65 km/h'], researchPoints: 2, spottingBonusBp: 250, completedTick: 30 }] },
    expedition: { contractCrews: [{ id: 'spotter_team', name: 'Spotter team', costCents: 150000, bonusDraws: 2, searchTicksDelta: 0 }, { id: 'veteran_trackers', name: 'Veteran trackers', costCents: 300000, bonusDraws: 3, searchTicksDelta: -2 }], status: 'idle', mode: 'auto', awaitingAdvance: false, biomes: ['grassland', 'scrub', 'woodland', 'rocky', 'wetland'], targetBiome: null, hiredCrewId: null, ticksRemainingInPhase: 0, lastReport: { biome: 'grassland', sightings: [{ species: 'chital', count: 3 }], returnedTick: 40, recalled: false, failedStage: null, captured: { species: 'chital', count: 1 } } },
    minigames: [{ kind: 'tracking', targetId: 'w1', progress: 55, target: 100 }, { kind: 'darting', targetId: 'w2', progress: 3, target: 8 }],
    ledgerMonthly: [
      { month: 0, incomeCents: 18_400_00, expenseCents: 12_150_00, netCents: 6_250_00, byCategory: {
        contracts: { incomeCents: 14_000_00, expenseCents: 0, netCents: 14_000_00 },
        wages: { incomeCents: 0, expenseCents: 9_000_00, netCents: -9_000_00 },
        feed: { incomeCents: 0, expenseCents: 3_150_00, netCents: -3_150_00 },
        sales: { incomeCents: 4_400_00, expenseCents: 0, netCents: 4_400_00 },
      } },
    ],
    researchTree: {
      active: { studyId: 'guest_tours_1', completesDay: 20, remainingDays: 9 },
      completed: ['guest_lodging_1'],
      tree: [
        { id: 'guest_lodging_1', name: 'Guest Comforts', description: 'Better beds, better rates.', branch: 'guest', tier: 0, costCents: 25_000_00, durationDays: 10, requires: [], missingPrereqs: [], effects: { lodging_rate_bonus_bp: 1500 }, state: 'completed' },
        { id: 'guest_tours_1', name: 'Guided Tour Craft', description: 'Word of mouth fills the roster.', branch: 'guest', tier: 0, costCents: 30_000_00, durationDays: 12, requires: [], missingPrereqs: [], effects: { tour_demand_bonus_bp: 1000 }, state: 'active' },
        { id: 'guest_gate_1', name: 'Premium Gate Experience', description: 'Guests accept a higher ticket.', branch: 'guest', tier: 1, costCents: 40_000_00, durationDays: 14, requires: ['guest_lodging_1'], missingPrereqs: [], effects: { ticket_price_tolerance_bp: 2000, zoo_satisfaction_gain: 2 }, state: 'available' },
        { id: 'guest_gate_2', name: 'Signature Safari Brand', description: 'A destination in itself.', branch: 'guest', tier: 2, costCents: 80_000_00, durationDays: 20, requires: ['guest_gate_1'], missingPrereqs: ['Premium Gate Experience'], effects: { ticket_price_tolerance_bp: 3000, tour_demand_bonus_bp: 1500 }, state: 'locked' },
      ],
    },
    contractOffers: [
      { id: 'c1', counterparty: 'Sunridge Zoo', species: 'axis deer', count: 2, minHealth: 80, pricePerHeadCents: 4_100_00, deadlineTick: 11 * 24 + 18 + 40 * 24, lapsePenaltyCents: 1_640_00 },
      { id: 'c2', counterparty: 'Highveld Exotics', species: 'blackbuck', count: 1, minHealth: 85, pricePerHeadCents: 6_800_00, deadlineTick: 11 * 24 + 18 + 55 * 24 },
    ],
    activeContracts: [
      { id: 'c0', counterparty: 'Okapi Park Trust', species: 'plains zebra', count: 1, minHealth: 90, pricePerHeadCents: 15_200_00, deadlineTick: 11 * 24 + 18 + 30 * 24, acceptedTick: 11 * 24 },
    ],
    research: {
      active: { studyId: 'habitat-mapping', startedTick: 11 * 24, completesTick: 11 * 24 + 3 * 24, progressBp: 4200 },
      completed: ['basic-husbandry'],
      catalog: [
        { id: 'habitat-mapping', name: 'Habitat Mapping', description: 'Scout-led survey. Raises spot radius in field mode.', costCents: 2_500_00, durationDays: 3 },
        { id: 'nutrition-plan', name: 'Nutrition Plan', description: 'Feed efficiency up, herd stress down.', costCents: 4_000_00, durationDays: 4 },
        { id: 'genetic-markers', name: 'Genetic Markers', description: 'Reveals trait odds before pairing.', costCents: 9_000_00, durationDays: 6 },
      ],
    },
  };
  const subs = new Set<(s: UiState) => void>();
  const emit = () => { subs.forEach((f) => { f(state); }); };

  // ---- Clock: live worker preferred, local fallback ----------------------
  let client: SimWorkerClient | null = null;
  let lastPollAt = performance.now();

  const poll = async () => {
    if (!client) return;
    try {
      const snap = await client.snapshot();
      if (snap.tick !== state.tick) {
        state = { ...state, tick: snap.tick };
        emit();
      }
      lastPollAt = performance.now();
    } catch (err) {
      console.warn('snapshot poll failed', err);
    }
  };

  if (pinnedTick === null) void import('../worker/client-factory')
    .then(({ createSimClient }) => createSimClient(DEFAULT_SEED))
    .then(({ client: c }) => {
      client = c;
      c.setSpeed(state.speed);
      void poll();
      // Interval kept alive for the app lifetime; no teardown needed in the shell.
      window.setInterval(() => { void poll(); }, 300);
      emit();
    })
    .catch((err: unknown) => {
      console.warn('sim worker unavailable, stub clock stays', err);
    });

  // Fallback stub clock: only ticks while the live client is absent.
  let acc = 0, last = performance.now();
  const step = (now: number) => {
    acc += (now - last) / 1000; last = now;
    const rate = TICKS_PER_SECOND_1X * state.speed;
    if (!client && rate > 0 && acc >= 1 / rate) {
      const ticks = Math.floor(acc * rate);
      acc -= ticks / rate;
      state = { ...state, tick: state.tick + ticks };
      emit();
    }
    requestAnimationFrame(step);
  };
  requestAnimationFrame(step);

  return {
    getState: () => state,
    isLive: () => client !== null,
    tickFloat() {
      // Interpolate between 300ms polls so the render pump is smooth.
      const elapsed = (performance.now() - lastPollAt) / 1000;
      return state.tick + elapsed * TICKS_PER_SECOND_1X * state.speed;
    },
    subscribe(fn) { subs.add(fn); return () => { subs.delete(fn); }; },
    dismissAwayNotice() { if (state.savingDisabled) return; state = { ...state, awayNotice: null }; },
    sendTracked(type, payload) { this.send(type, payload); return Promise.resolve(null); },
    send(type, payload) {
      const cmd: SimCommand = { id: crypto.randomUUID(), tick: state.tick, type, payload, source: 'player' };
      if (type === 'time.setSpeed') {
        const speed = (payload as { speed: Speed }).speed;
        state = { ...state, speed };
        lastPollAt = performance.now();
        if (client) { try { client.setSpeed(speed); } catch (err) { console.warn('setSpeed rejected', err); } }
        emit();
        return;
      }
      // Stub field reactions until Mgmt-Sim owns noise/visibility/inventory:
      // crouch drops both meters, standing restores them; placing a trap
      // spends one from the field kit.
      if (type === 'field.crouch') {
        const c = (payload as { on?: boolean; crouched?: boolean }).on
          ?? (payload as { crouched?: boolean }).crouched ?? false;
        state = { ...state, field: { ...state.field, stealth: c ? { noise: 0.15, visibility: 0.2 } : { noise: 0.4, visibility: 0.55 } } };
        emit();
      }
      if (type === 'trap.place' && state.field.trapsRemaining > 0) {
        state = { ...state, field: { ...state.field, trapsRemaining: state.field.trapsRemaining - 1 } };
        emit();
      }
      if (client) {
        const wire = COMMAND_ALIASES[type] ?? type;
        client.command(wire, cmd.payload, 'player').catch((err: unknown) => {
          console.warn(`command ${wire} rejected`, err);
          const reason = err instanceof Error && err.message ? `: ${err.message}` : '';
          { const msg = `command_rejected: ${wire}${reason}`; state = { ...state, alerts: [...state.alerts, msg], alertEvents: [...state.alertEvents, { day: Math.floor(state.tick / 24), type: 'command_rejected', refId: null, message: msg }] }; }
          emit();
        });
      }
      emit();
    },
    monetization: {
      // MonetizationHooks (social.ts): no-op in v1, entitlement always false.
      onEvent() { /* hooks only - nothing monetized */ },
      isPremiumEntitlementActive() { return false; },
    },
  };
}

/**
 * Dedupe alerts by a normalized key (case, whitespace, and digit runs
 * collapsed) so a per-tick repeating condition ("Cash below 50000 cents")
 * renders once. Keeps the first occurrence, preserves order. Sim-Core owns
 * firing once per condition change; this is the UI's safety net.
 */
/** P128 player-facing offline summary: format the worker's away result once.
 *  null below one game hour of away time so quick reloads stay quiet. */
export function formatAwayNotice(away: OfflineCatchup | undefined): string | null {
  if (away === undefined || away.ticks < 1) return null;
  const days = Math.floor(away.ticks / DAY_HOURS);
  const hours = away.ticks % DAY_HOURS;
  const span = [
    days > 0 ? `${String(days)} day${days === 1 ? '' : 's'}` : '',
    hours > 0 ? `${String(hours)} hour${hours === 1 ? '' : 's'}` : '',
  ].filter((part) => part !== '').join(' ');
  const realH = Math.floor(away.realSeconds / 3600);
  const realM = Math.round((away.realSeconds % 3600) / 60);
  const real = [
    realH > 0 ? `${String(realH)}h` : '',
    realH === 0 || realM > 0 ? `${String(realM)}m` : '',
  ].filter((part) => part !== '').join(' ');
  return `While you were away: ${span} passed on the reserve (${real} real time)${away.capped ? ` - away time is capped at ${String(OFFLINE_CATCHUP_MAX_TICKS / DAY_HOURS)} days` : ''}.`;
}

export function dedupeAlerts(alerts: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const a of alerts) {
    const key = a.toLowerCase().replace(/\d+/g, '#').replace(/\s+/g, ' ').trim();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(a);
  }
  return out;
}

/** Capitalise the first letter ("axis deer" becomes "Axis deer"). */
export function cap(s: string): string { return s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
/** Sim species ids look like "sika_deer". Show "Sika Deer". */
export function speciesLabel(species: string): string { return species.split('_').filter((w) => w.length > 0).map(cap).join(' '); }
/** Sim coat ids look like "axis_spotted" or "blackbuck_normal". Show "Spotted" or "Normal": no underscores, no repeated species. */
export function coatLabel(species: string, coat: string): string {
  const words = coat.replace(/_/g, ' ');
  const sp = species.replace(/_/g, ' ').toLowerCase();
  const first = sp.split(' ')[0] ?? sp;
  const lower = words.toLowerCase();
  const rest = lower.startsWith(sp + ' ') ? words.slice(sp.length + 1) : lower.startsWith(first + ' ') ? words.slice(first.length + 1) : words;
  return cap(rest);
}
/** The name shown when the player has not named an animal: "Axis deer #1", not the raw "axis_deer an-1". */
function defaultAnimalName(species: string, id: string): string {
  const m = /^an-(\d+)$/.exec(id);
  return `${cap(species)} ${m ? `#${m[1]}` : id}`;
}
export function fmtMoney(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 });
}
const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
export function fmtClock(tick: number): string {
  const totalHours = Math.floor(tick);
  const dayOfYear = Math.floor(totalHours / DAY_HOURS);
  const hour = totalHours % DAY_HOURS;
  // Game year starts Oct 1 (flavor matching mockup "OCT 12 - 06:32PM").
  const monthIdx = (9 + Math.floor(dayOfYear / 30)) % 12;
  const day = (dayOfYear % 30) + 1;
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  const ampm = hour < 12 ? 'AM' : 'PM';
  const month = MONTHS[monthIdx] ?? 'JAN';
  return `${month} ${day} - ${String(h12).padStart(2, '0')}:00${ampm}`;
}

/* ---------- Lane 1 worker-backed bridge (live sim) ---------- */
import { createSimClient, type SimClientHandle } from '../worker/client-factory';
import { AutosaveScheduler } from './autosave';
import { decodeEntityBuffer } from '../sim/core/snapshot-sections';
import { OFFLINE_CATCHUP_MAX_TICKS, type OfflineCatchup } from '../save/offline-policy';

interface UistAnimal {
  id: string; species: string; sex: 'M' | 'F'; health: number; stress: number;
  coat: string; traits: string[]; enclosureId: string | null; name: string | null;
  bornTick?: number; // absent on older workers
  temperament?: string | null;
}
/** Mgmt-Sim's detail slice (PR #23, 'mgmt' system collectUiState). */
interface UistMgmtDetail {
  ranchOperations?:ReturnType<typeof import('../sim/systems/ranchOperations').projectRanchOperations>;
  animals?: Array<{ id: string; ill?: boolean; quarantined?: boolean; hybrid?: string | null; knowledge?: UiKnowledge; maturityDays?:number }>;
  parcels?: Array<{ id: string; hectares: number; landClass: string; distanceKm: number; priceCents: number; owned: boolean }>;
  fenceLayouts?: Record<string, UiFenceLayout>;
  penCells?: Record<string, number[]>;
  enclosures?: Array<{ id: string; parcelId: string; type: string; areaHa: number; capacity: number; animalCount: number; vaccinated?: boolean; outbreakReported?: boolean; outbreakConcealed?: boolean; illCount?: number }>;
  dealerOffers?: UiDealerOffer[];
  staff?: UiStaffMember[];
  staffRoles?: UiStaffHireCost[]; // prehire costs only, not unaudited duty strings
  startModes?: Record<string, number>;
  trapInventory?: Record<string, number>;
  loan?: UiLoan | null;
  ledgerMonthly?: UiMonthBucket[];
  pregnancies?: Array<{ damId: string; sireId: string; dueTick?: number; dueDay?: number }>;
  decisions?: UiDecision[]; realism?: number; breeding?: UiBreeding;
  morphGoals?: UiMorphGoal[]; hybridGoals?: UiHybridGoal[];
  auctionTerms?: UiBusiness['auctionTerms'];
  workforce?: UiBusiness['workforce'];
  guestMap?: UiBusiness['guestMap'];
  partners?: UiBusiness['partners']; meat?: UiBusiness['meat']; specials?: UiBusiness['specials']; genetics?: UiBusiness['genetics']; rewilding?: UiBusiness['rewilding']; finance?: UiBusiness['finance']; fences?: UiBusiness['fences']; staffDepth?: UiBusiness['staffDepth']; season?: UiOutlook['season']; auction?: UiBusiness['auction']; crateIntakePending?: UiBusiness['crateIntakePending']; challenges?: UiBusiness['challenges']; timeshare?: UiBusiness['timeshare']; camping?: UiBusiness['camping']; taxes?: UiBusiness['taxes']; zoo?: UiBusiness['zoo']; restoration?: UiBusiness['restoration']; vegetation?: UiBusiness['vegetation']; stock?: UiStockBoard;
  reputation?: Record<string, number>; repCauses?: Array<{ tick: number; track: string; delta: number; reason: string }>; milestones?: UiStanding['milestones']; licenses?: UiStanding['licenses']; education?: UiStanding['education'];
  grants?: UiStanding['grants']; speciesFee?: UiStanding['speciesFee']; jurisdictions?: UiStanding['jurisdictions']; importHolds?: string[]; herdSales?: import('../sim/systems/herdSales').HerdSalesState; treatmentReceipts?: UiTreatmentReceipt[]; moveOutcomes?: UiMoveOutcome[];
  exportCounts?: Record<string, number>; tours?: UiStanding['tours']; lodging?: UiStanding['lodging']; events?: UiStanding['events']; eventCatalog?: UiStanding['eventCatalog']; regions?: UiStanding['regions']; treatCostCents?: number; vaccineCostCents?: number;
  droughtActive?: boolean;
  identity?: UiIdentity;
  contractOffers?: UiContractOffer[]; activeContracts?: UiContractOffer[];
  research?: UiResearch | UiResearchTree;
}
interface UistMgmt {
  ranchOperations?:ReturnType<typeof import('../sim/systems/ranchOperations').projectRanchOperations>;
  speed?: Speed;
  cashCents: number; mode: 'easy' | 'standard' | 'hard' | null; tick: number;
  animals: UistAnimal[]; staffCount: number; staffActive: number;
  overworked: boolean; alerts: string[]; alertsDetailed?: UiAlertEvent[]; dealerOutcomes?: UiDealerOutcome[]; ranchPath: string | null;
  // Economy-loop projections (PR #22); optional so older workers still decode.
  licence?: boolean; insurance?: boolean; loan?: UiLoan | null;
  parcels?: UiParcel[]; enclosures?: UiEnclosure[];
  dealerOffers?: UiDealerOffer[]; dealerNextRefreshTick?: number;
  staff?: UiStaffMember[]; traps?: UiTrap[];
  enclosureTypes?: Record<string, UiEnclosureType>; trapTypes?: string[];
  startModes?: string[];
  identity?: UiIdentity;
}


function mapUiState(mgmt: UistMgmt, prev: UiState, detail?: UistMgmtDetail, overlay?: { cells?: Array<{ forageKg: number | null }> }, minigames?: UiMinigame[], extra?: { observe?: UiObserve | undefined; expedition?: UiExpedition | undefined; hunts?: UiHunts | undefined; poaching?: UiPoaching | undefined; outcome?: UiMinigameOutcome | null | undefined; capture?: UiFieldCapture | undefined; parkTraps?: UiTrap[] | undefined; facilities?: string[] | undefined; staffBeds?: number | undefined; facilityCatalog?: UiFacilityDef[] | undefined; financeDepth?: UiFinanceDepth | undefined; studbook?: UiStudbook | undefined; priceHistory?: UiPriceHistory | undefined; dashboard?: UiDashboard | undefined; neighbors?: UiNeighbors | undefined; story?: UiStory | undefined; lifelog?: UiLifeLog | undefined }): UiState {
  // Detail slice wins where it is richer; hud slice fills the rest.
  const parcels: UiParcel[] = detail?.parcels
    ? detail.parcels.map((pc) => ({ id: pc.id, hectares: pc.hectares, landClass: pc.landClass, distanceKm: pc.distanceKm, price: pc.priceCents, owned: pc.owned }))
    : mgmt.parcels ?? prev.parcels;
  const fenceLayouts: Record<string, UiFenceLayout> = detail?.fenceLayouts ?? prev.fenceLayouts;
  const penCells: Record<string, number[]> = detail?.penCells ?? prev.penCells;
  const dealerOffers: UiDealerOffer[] = detail?.dealerOffers
    ? detail.dealerOffers.map((o) => ({ ...o }))
    : mgmt.dealerOffers ?? prev.dealerOffers;
  const startModes = detail?.startModes ? Object.keys(detail.startModes) : mgmt.startModes ?? prev.startModes;
  const startModeCash = detail?.startModes ?? prev.startModeCash;
  const trapsRemaining = detail?.trapInventory
    ? Object.values(detail.trapInventory).reduce((a, b) => a + b, 0)
    : prev.field.trapsRemaining;
  const detailById = new Map((detail?.animals ?? []).map((d) => [d.id, d] as const));
  const animals: UiAnimal[] = mgmt.animals.map((a) => {
    const knowledgeOf = detailById.get(a.id)?.knowledge;
    return {
    id: a.id,
    name: a.name ?? defaultAnimalName(a.species.replace(/_/g, ' '), a.id),
    species: a.species.replace(/_/g, ' '),
    speciesId: a.species,
    sex: a.sex,
    ...(detailById.get(a.id)?.maturityDays!==undefined?{maturityDays:detailById.get(a.id)?.maturityDays as number}:{}),
    ...(detailById.get(a.id)?.hybrid ? { hybrid: detailById.get(a.id)?.hybrid as string } : {}),
    ...(detailById.get(a.id)?.knowledge ? { knowledge: detailById.get(a.id)?.knowledge as UiKnowledge } : {}),
    coat: a.coat,
    ...(a.bornTick !== undefined ? { bornTick: a.bornTick } : {}),
    temperament: a.temperament ? cap(a.temperament) : 'Not recorded',
    traits: a.traits,
    rare: false, // rarity display lands with the Herd screen data pass
    health: a.health,
    stress: a.stress,
    enclosure: a.enclosureId,
    biome: '',
    rarity: 'common',
    ill: detailById.get(a.id)?.ill === true,
    ...(detailById.get(a.id)?.quarantined !== undefined ? { quarantined: detailById.get(a.id)?.quarantined === true } : {}),
    ...(knowledgeOf !== undefined ? { knowledge: knowledgeOf } : {}),
  };
  });
  return {
    ...prev,
    tick: mgmt.tick,
    speed: mgmt.speed ?? prev.speed,
    ...((detail?.ranchOperations??mgmt.ranchOperations)?{ranchOperations:detail?.ranchOperations??mgmt.ranchOperations}:{}),
    cashCents: mgmt.cashCents,
    animals,
    staffActive: mgmt.staffActive,
    staffCapacity: mgmt.staffCount,
    overworked: mgmt.overworked,
    alerts: mgmt.alerts,
    awayNotice: prev.awayNotice,
    savingDisabled: prev.savingDisabled ?? false,
    alertEvents: mgmt.alertsDetailed ?? [],
    dealerOutcomes: mgmt.dealerOutcomes ?? [],
    parcels,
    fenceLayouts,
    penCells,
    dealerOffers,
    startModes,
    startModeCash,
    field: { ...prev.field, trapsRemaining, ...(extra?.capture?.world ? {stealth:{noise:extra.capture.world.noise/100,visibility:extra.capture.world.visibility/100}} : {}) },
    fieldWorld: extra?.capture?.world ?? prev.fieldWorld ?? null,
    licence: mgmt.licence ?? prev.licence,
    insurance: mgmt.insurance ?? prev.insurance,
    loan: mgmt.loan !== undefined ? mgmt.loan : prev.loan,
    enclosures: mgmt.enclosures ?? prev.enclosures,
    dealerNextRefreshTick: mgmt.dealerNextRefreshTick ?? prev.dealerNextRefreshTick,
    staff: projectStaffRoster(mgmt.staff, detail?.staff, prev.staff),
    staffHireCosts: projectStaffHireCosts(detail?.staffRoles),
    herdSales: detail?.herdSales ?? {reviews:[],receipts:[],processed:[]},
    treatmentReceipts: detail?.treatmentReceipts ?? [],
    traps: extra?.parkTraps ?? mgmt.traps ?? prev.traps,
    facilitiesBuilt: extra?.facilities ?? prev.facilitiesBuilt, staffBeds: extra?.staffBeds ?? prev.staffBeds,
    facilityCatalog: extra?.facilityCatalog ?? prev.facilityCatalog,
    financeDepth: extra?.financeDepth ?? prev.financeDepth,
    studbook: extra?.studbook ?? prev.studbook,
    priceHistory: extra?.priceHistory ?? prev.priceHistory,
    story: extra?.story ?? prev.story,
    lifelog: extra?.lifelog ?? prev.lifelog,
    dashboard: extra?.dashboard ?? prev.dashboard,
    neighbors: extra?.neighbors ?? prev.neighbors,
    enclosureTypes: mgmt.enclosureTypes ?? prev.enclosureTypes,
    trapTypes: mgmt.trapTypes ?? prev.trapTypes,
    ledgerMonthly: detail?.ledgerMonthly ?? prev.ledgerMonthly,
    pregnancies: detail?.pregnancies
      ? detail.pregnancies.map((p) => { const dueTick = p.dueTick ?? (p.dueDay ?? 0) * 24; return { damId: p.damId, sireId: p.sireId, dueTick, dueDay: p.dueDay ?? Math.floor(dueTick / 24) }; })
      : prev.pregnancies,
    decisions: detail?.decisions ?? prev.decisions,
    moveOutcomes: detail?.moveOutcomes ?? prev.moveOutcomes,
    breeding: detail?.breeding ?? prev.breeding,
    business: detail && (detail.auctionTerms || detail.workforce || detail.guestMap || detail.partners || detail.meat || detail.specials || detail.genetics || detail.morphGoals || detail.hybridGoals || detail.rewilding || detail.finance || detail.fences || detail.stock || detail.zoo || detail.restoration || detail.vegetation || detail.taxes || detail.camping || detail.auction || detail.crateIntakePending || detail.challenges || detail.season || detail.staffDepth || detail.timeshare)
      ? { ...(detail.auctionTerms?{auctionTerms:detail.auctionTerms}:{}), ...(detail.workforce?{workforce:detail.workforce}:{}), ...(detail.guestMap ? {guestMap:detail.guestMap}:{}), ...(detail.partners ? { partners: detail.partners } : {}), ...(detail.meat ? { meat: detail.meat } : {}), ...(detail.specials ? { specials: detail.specials } : {}), ...(detail.genetics ? { genetics: detail.genetics } : {}), ...(detail.rewilding ? { rewilding: detail.rewilding } : {}), ...(detail.finance ? { finance: detail.finance } : {}), ...(detail.fences ? { fences: detail.fences } : {}), ...(detail.stock ? { stock: detail.stock } : {}), ...(detail.zoo ? { zoo: detail.zoo } : {}), ...(detail.morphGoals ? { morphGoals: detail.morphGoals } : {}), ...(detail.hybridGoals ? { hybridGoals: detail.hybridGoals } : {}), ...(detail.restoration ? { restoration: detail.restoration } : {}), ...(detail.vegetation ? { vegetation: detail.vegetation } : {}), ...(detail.taxes ? { taxes: detail.taxes } : {}), ...(detail.camping ? { camping: detail.camping } : {}), ...(detail.auction ? { auction: detail.auction } : {}), ...(detail.crateIntakePending ? { crateIntakePending: detail.crateIntakePending } : {}), ...(detail.challenges ? { challenges: detail.challenges } : {}), ...(detail.season ? { season: detail.season } : {}), ...(detail.staffDepth ? { staffDepth: detail.staffDepth } : {}), ...(detail.timeshare ? { timeshare: detail.timeshare } : {}) }
      : prev.business,
    realism: detail?.realism ?? prev.realism,
    standing: detail ? {
      ...(detail.reputation ? { reputation: detail.reputation } : {}), ...(detail.milestones ? { milestones: detail.milestones } : {}),
      ...(detail.licenses ? { licenses: detail.licenses } : {}), ...(detail.education ? { education: detail.education } : {}),
      ...(detail.grants ? { grants: detail.grants } : {}), ...(detail.speciesFee ? { speciesFee: detail.speciesFee } : {}),
      ...(detail.jurisdictions ? { jurisdictions: detail.jurisdictions } : {}), ...(detail.importHolds ? { importHolds: detail.importHolds } : {}),
      ...(detail.exportCounts ? { exportCounts: detail.exportCounts } : {}), ...(detail.tours ? { tours: detail.tours } : {}),
      ...(detail.lodging ? { lodging: detail.lodging } : {}), ...(detail.eventCatalog ? { eventCatalog: detail.eventCatalog } : {}), ...(detail.events ? { events: detail.events } : {}),
      ...(detail.regions ? { regions: detail.regions } : {}), ...(detail.treatCostCents !== undefined ? { treatCostCents: detail.treatCostCents } : {}), ...(detail.vaccineCostCents !== undefined ? { vaccineCostCents: detail.vaccineCostCents } : {}),
    } : prev.standing,
    identity: mgmt.identity ?? detail?.identity ?? prev.identity,
    loanTerms: (mgmt as { loanTerms?: { takeAprPercent: number; maxPrincipalCents: number } }).loanTerms ?? prev.loanTerms,
    hunts: extra?.hunts ?? prev.hunts,
    poaching: extra?.poaching ?? prev.poaching,
    fieldCapture: extra?.capture ?? prev.fieldCapture,
    minigameOutcome: extra?.outcome !== undefined ? extra.outcome : prev.minigameOutcome,
    observe: extra?.observe ?? prev.observe,
    expedition: extra?.expedition ?? prev.expedition,
    minigames: minigames ?? prev.minigames,
    pasture: overlay?.cells ? { forageKg: overlay.cells.map((c) => c.forageKg), droughtActive: detail?.droughtActive ?? false } : prev.pasture,
    contractOffers: detail?.contractOffers ?? prev.contractOffers,
    activeContracts: detail?.activeContracts ?? prev.activeContracts,
    research: detail?.research && 'catalog' in detail.research ? detail.research : prev.research,
    forecast: projectForecast((detail as { advisors?: unknown } | undefined)?.advisors),
    advisors: projectAdvisors((detail as { advisors?: unknown } | undefined)?.advisors),
    researchTree: (mgmt as { researchTree?: UiResearchTree }).researchTree ?? (detail?.research && 'tree' in detail.research ? detail.research : prev.researchTree),
  };
}

/**
 * Live bridge: UI talks to the sim worker (Lane 1) instead of the stub.
 * Same SimBridge interface - screens need no changes.
 */
export function createWorkerBridge(): SimBridge {
  // Debug capture pin: ?tick=N freezes the HUD clock + world lighting so QA
  // can frame day/dusk/night on deployed builds. Sim keeps running underneath.
  const pinnedTick = (() => {
    if (typeof location === 'undefined') return null;
    const raw = new URLSearchParams(location.search).get('tick');
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  })();
  let state: UiState = {
    penCells: {},
    tick: 0, speed: 1, cashCents: 0, animals: [], staffRoles: [],
    staffActive: 0, staffCapacity: 0, deployMax: 0, overworked: false, alerts: [], awayNotice: null, alertEvents: [], dealerOutcomes: [], moveOutcomes: [],
    field: {
      goal: 'Find fresh chital tracks',
      trapsRemaining: 4,
      stealth: { noise: 0.4, visibility: 0.55 },
    },
    ...EMPTY_ECONOMY,
    licence: true,
    loan: { principal: 500_00, aprPercent: 6 },
    parcels: [
      { id: 'p1', hectares: 12, landClass: 'Savanna', distanceKm: 0.4, price: 0, owned: true },
      { id: 'p2', hectares: 20, landClass: 'Scrub', distanceKm: 1.1, price: 450_000_00, owned: false },
      { id: 'p3', hectares: 8, landClass: 'Wetland', distanceKm: 2.3, price: 610_000_00, owned: false },
    ],
    enclosures: [
      { id: 'e1', parcelId: 'p1', type: 'paddock', areaHa: 4, fencePerimeterM: 800, waterPoints: 1, animalCount: 5 },
    ],
    dealerOffers: [
      { id: 'o1', species: 'axis deer', sex: 'F', ageDays: 620, health: 92, priceCents: 3_200_00 },
      { id: 'o2', species: 'blackbuck', sex: 'M', ageDays: 840, health: 88, priceCents: 4_750_00 },
      { id: 'o3', species: 'plains zebra', sex: 'F', ageDays: 1100, health: 95, priceCents: 12_900_00 },
    ],
    staff: [
      { id: 's1', role: 'ranger', assignedTo: 'e1', fatigue: 62, skill: 62, morale: 55, wagePerDay: 120_00 },
      { id: 's2', role: 'veterinarian', assignedTo: null, fatigue: 20, skill: 74, morale: 70, manager: true, wagePerDay: 225_00 },
      { id: 's3', role: 'scout', assignedTo: null, fatigue: 35, skill: 50, morale: 68, wagePerDay: 100_00 },
    ],
    traps: [{ id: 't1', parcelId: 'p1', type: 'pitfall', baited: true }],
    enclosureTypes: {
      paddock: { base_cost_cents: 0, fence_cost_per_m_cents: 2000, water_point_cost_cents: 150000, density_per_ha: 3, groups: ['hoofstock', 'ratite'] },
      pasture: { base_cost_cents: 50000, fence_cost_per_m_cents: 500, water_point_cost_cents: 150000, density_per_ha: 6, groups: ['hoofstock'] },
    },
    trapTypes: ['pitfall', 'snare', 'dart'],
    ledgerMonthly: [
      { month: 0, incomeCents: 18_400_00, expenseCents: 12_150_00, netCents: 6_250_00, byCategory: {
        contracts: { incomeCents: 14_000_00, expenseCents: 0, netCents: 14_000_00 },
        wages: { incomeCents: 0, expenseCents: 9_000_00, netCents: -9_000_00 },
        feed: { incomeCents: 0, expenseCents: 3_150_00, netCents: -3_150_00 },
        sales: { incomeCents: 4_400_00, expenseCents: 0, netCents: 4_400_00 },
      } },
    ],
    researchTree: {
      active: { studyId: 'guest_tours_1', completesDay: 20, remainingDays: 9 },
      completed: ['guest_lodging_1'],
      tree: [
        { id: 'guest_lodging_1', name: 'Guest Comforts', description: 'Better beds, better rates.', branch: 'guest', tier: 0, costCents: 25_000_00, durationDays: 10, requires: [], missingPrereqs: [], effects: { lodging_rate_bonus_bp: 1500 }, state: 'completed' },
        { id: 'guest_tours_1', name: 'Guided Tour Craft', description: 'Word of mouth fills the roster.', branch: 'guest', tier: 0, costCents: 30_000_00, durationDays: 12, requires: [], missingPrereqs: [], effects: { tour_demand_bonus_bp: 1000 }, state: 'active' },
        { id: 'guest_gate_1', name: 'Premium Gate Experience', description: 'Guests accept a higher ticket.', branch: 'guest', tier: 1, costCents: 40_000_00, durationDays: 14, requires: ['guest_lodging_1'], missingPrereqs: [], effects: { ticket_price_tolerance_bp: 2000, zoo_satisfaction_gain: 2 }, state: 'available' },
        { id: 'guest_gate_2', name: 'Signature Safari Brand', description: 'A destination in itself.', branch: 'guest', tier: 2, costCents: 80_000_00, durationDays: 20, requires: ['guest_gate_1'], missingPrereqs: ['Premium Gate Experience'], effects: { ticket_price_tolerance_bp: 3000, tour_demand_bonus_bp: 1500 }, state: 'locked' },
      ],
    },
    contractOffers: [
      { id: 'c1', counterparty: 'Sunridge Zoo', species: 'axis deer', count: 2, minHealth: 80, pricePerHeadCents: 4_100_00, deadlineTick: 11 * 24 + 18 + 40 * 24, lapsePenaltyCents: 1_640_00 },
      { id: 'c2', counterparty: 'Highveld Exotics', species: 'blackbuck', count: 1, minHealth: 85, pricePerHeadCents: 6_800_00, deadlineTick: 11 * 24 + 18 + 55 * 24 },
    ],
    activeContracts: [
      { id: 'c0', counterparty: 'Okapi Park Trust', species: 'plains zebra', count: 1, minHealth: 90, pricePerHeadCents: 15_200_00, deadlineTick: 11 * 24 + 18 + 30 * 24, acceptedTick: 11 * 24 },
    ],
    research: {
      active: { studyId: 'habitat-mapping', startedTick: 11 * 24, completesTick: 11 * 24 + 3 * 24, progressBp: 4200 },
      completed: ['basic-husbandry'],
      catalog: [
        { id: 'habitat-mapping', name: 'Habitat Mapping', description: 'Scout-led survey. Raises spot radius in field mode.', costCents: 2_500_00, durationDays: 3 },
        { id: 'nutrition-plan', name: 'Nutrition Plan', description: 'Feed efficiency up, herd stress down.', costCents: 4_000_00, durationDays: 4 },
        { id: 'genetic-markers', name: 'Genetic Markers', description: 'Reveals trait odds before pairing.', costCents: 9_000_00, durationDays: 6 },
      ],
    },
  };
  const subs = new Set<(s: UiState) => void>();
  const emit = () => { subs.forEach((f) => { f(state); }); };

  let handle: SimClientHandle | null = null;
  let ready = false;
  // Wired to the AutosaveScheduler once the client is ready; no-op before.
  let markDirty: () => void = () => undefined;
  // Never autosave a session with no active scenario: a dead $0 session
  // must not clobber a good save. Flipped on by pumped snapshots (mode set).
  let scenarioActive = false;
  // Local guard: dispatch scenario.start exactly once per session.
  let scenarioStarted = false;
  let lastPollAt = performance.now();
  const queued: Array<() => void> = [];
  const whenReady = (fn: () => void) => { if (ready) fn(); else queued.push(fn); };

  void (async () => {
    handle = await createSimClient(20261021);
    const configureDurability = () => {
      if (!handle) return;
      handle.client.checkpointNow = () => Date.now();
      handle.client.onPersistenceError = () => {
        const message = 'Your action was applied, but this device could not save it. Keep this tab open and export your game from Settings before leaving.';
        state = { ...state, awayNotice: message, alerts: [...state.alerts, message] };
        emit();
      };
      handle.client.commandCheckpointEnabled = !recoveryWriteBlocked;
      handle.client.beforeRanchReplacement = async () => {
        if (recoveryWriteBlocked) throw new Error('Saving is off to protect your original save files. Nothing was replaced.');
        if (!handle || !scenarioActive) return;
        const checkpoint = await handle.client.exportCurrent(Date.now());
        if (!preserveReplacementSave(localStorage, checkpoint)) throw new Error('Could not protect the current game. Nothing was replaced.');
      };
    };
    // Resume: slot 0 is the autosave. Reloading mid-game restores the full
    // economy (cash, herd, tick) instead of a scenario-less $0 restart.
    const AUTOSAVE_SLOT = 0;
    let restoredSave = false;
    let awaySummary: string | null = null;
    // Report recovery failure without silently replacing the player's continuity.
    let restoreFailedNotice: string | null = null;
    let recoveryWriteBlocked = false;
    try {
      if (handle.client.listSlots()[AUTOSAVE_SLOT]) {
        const res = await handle.client.load(AUTOSAVE_SLOT, Date.now());
        restoredSave = true;
        // P128 player-facing away summary: the worker already returns the
        // bounded catch-up; the UI formats it once and never re-derives it.
        awaySummary = formatAwayNotice(res.away);
      }
    } catch (err) {
      // Archive BEFORE backup promotion: promotion itself overwrites primary.
      // A failed archive blocks every fresh-ranch write, including unload flush.
      let preserved = false;
      try { preserved = preserveFailedSave(globalThis.localStorage, AUTOSAVE_SLOT); } catch { /* storage unavailable */ }
      recoveryWriteBlocked = !preserved;
      if (preserved) {
        try {
          handle.client.restoreBackup(AUTOSAVE_SLOT);
          const res = await handle.client.load(AUTOSAVE_SLOT, Date.now());
          restoredSave = true;
          // P129: the fallback must come with a message, not a silent swap.
          const away = formatAwayNotice(res.away);
          awaySummary = 'Your latest save was damaged, so the previous autosave was loaded.' + (away === null ? '' : ' ' + away);
        } catch {
          console.warn('autosave restore failed, starting fresh', err);
          restoreFailedNotice = 'Save could not open. New ranch started. Original files kept on this device.';
        }
      } else {
        console.warn('autosave recovery could not preserve originals; saving disabled', err);
        restoreFailedNotice = 'Save could not open. Saving is OFF to protect originals. This session will not save.';
      }
    }
    configureDurability();
    // A restored save means the scenario is already running in the worker.
    // Learn that NOW from one authoritative snapshot instead of racing the
    // 4Hz pump against the self-heal timer below: when the first pump lands
    // after the 1.2s timer (slow worker, large save), scenarioActive is still
    // false and the heal blind-fires a redundant scenario.start whose
    // 'scenario already started' rejection pollutes the player alert feed.
    // Snapshot unreadable: assume running - autosaves are only ever written
    // with an active scenario, so skipping the heal is the safe default.
    if (restoredSave) {
      try {
        const snap = await handle.client.snapshot();
        const hud = decodeEntityBuffer(snap.entities as Uint8Array).ui.hud as UistMgmt | undefined;
        scenarioActive = hud ? hud.mode !== null : true;
        // Project the restored state NOW from this authoritative snapshot:
        // without it, a restored player stares at a false empty ($0) reserve
        // until the first 4Hz pump lands (seconds of render-load starvation
        // on phones). Progress is never lost - the HUD was just late.
        if (hud) {
          try { state = mapUiState(hud, state); emit(); } catch { /* projection is best-effort; the pump owns state */ }
        }
      } catch {
        scenarioActive = true;
      }
    }
    if (awaySummary !== null) { state = { ...state, awayNotice: awaySummary }; emit(); }
    // Reuses the one-shot banner slot; deliberately NOT tied to the
    // catch-up autosave below (that one consumes real away time only).
    if (restoreFailedNotice !== null) { state = { ...state, awayNotice: restoreFailedNotice, savingDisabled: recoveryWriteBlocked }; emit(); }
    const autosave = () => {
      if (!scenarioActive || recoveryWriteBlocked) return;
      handle?.client.save(AUTOSAVE_SLOT, Date.now()).catch(() => undefined);
    };
    // Consume applied away time immediately: without a prompt save, a reload
    // before the cadence save would re-apply the same catch-up and re-announce
    // it. Saving right after the catch-up makes the summary one-shot.
    if (awaySummary !== null) autosave();
    // Durability policy: interval cadence + command-accelerated saves shrink
    // the unload loss window from up to a full interval to a few seconds of
    // play after the last action (see src/ui/autosave.ts).
    const autosaveScheduler = new AutosaveScheduler(autosave);
    autosaveScheduler.start();
    markDirty = () => { autosaveScheduler.markDirty(); };
    // Unload path: a save STARTED here needs a worker round-trip that races
    // page teardown and usually loses - synchronously flush the newest
    // envelope we already hold first (guaranteed), then attempt a fresh one
    // (best-effort; it lands whenever the browser grants the round-trip).
    const unloadSave = () => {
      if (recoveryWriteBlocked) return;
      handle?.client.flushCachedSave(AUTOSAVE_SLOT);
      autosave();
    };
    window.addEventListener('pagehide', unloadSave);
    // Interruption policy (P128): mobile browsers can skip pagehide when the
    // app is backgrounded by a call or app switch; hidden is the last chance.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') unloadSave();
    });
    ready = true;
    // Self-heal a dead session: restore failed/absent but the player already
    // picked a path (localStorage). Re-dispatch the start so they never land
    // on a scenario-less $0 reserve.
    window.setTimeout(() => {
      // An explicit player start (difficulty picker) latched scenarioStarted
      // when it dequeued; treat that as "session handled" too. scenarioActive
      // alone lags: the first pump can land after this window on slow workers,
      // and the blind re-dispatch is rejected as 'scenario already started'.
      if (scenarioActive || scenarioStarted) return;
      let path: string | null = null;
      let startMode = 'standard';
      let scenarioId: string | null = null;
      try { path = localStorage.getItem('ea.path'); startMode = localStorage.getItem('ea.mode') ?? 'standard'; scenarioId = localStorage.getItem('ea.scenario'); } catch { /* private mode */ }
      if (path) {
        // No path pick any more: a started session only needs scenario.start with its saved difficulty or catalog scenario.
        handle?.client.command('scenario.start', scenarioId === null ? { mode: startMode } : { scenario: scenarioId, mode: startMode }, 'player').catch((err: unknown) => {
          const reason = err instanceof Error && err.message ? `: ${err.message}` : '';
          { const msg = `command_rejected: scenario.start${reason}`; state = { ...state, alerts: [...state.alerts, msg], alertEvents: [...state.alertEvents, { day: Math.floor(state.tick / 24), type: 'command_rejected', refId: null, message: msg }] }; }
          emit();
        });
      }
    }, 1200);
    for (const fn of queued.splice(0)) fn();
    // Resilience (P029): after ~2s of consecutive pump failures the worker
    // is presumed wedged - recreate it and restore from the autosave.
    let pumpFailures = 0;
    let recovering = false;
    const recoverWorker = async () => {
      if (recovering) return;
      recovering = true;
      console.warn('sim worker unresponsive; restoring from autosave');
      try { handle?.worker.terminate(); } catch { /* already dead */ }
      handle = null;
      ready = false;
      try {
        handle = await createSimClient(20261021);
        configureDurability();
        try {
          if (handle.client.listSlots()[AUTOSAVE_SLOT]) {
            await handle.client.load(AUTOSAVE_SLOT, Date.now());
          }
        } catch {
          try {
            if (recoveryWriteBlocked) throw new Error('original saves protected; recovery writes disabled');
            handle.client.restoreBackup(AUTOSAVE_SLOT);
            await handle.client.load(AUTOSAVE_SLOT, Date.now());
          } catch (err) {
            console.warn('recovery restore failed, running fresh', err);
          }
        }
        ready = true;
        pumpFailures = 0;
        for (const fn of queued.splice(0)) fn();
      } finally {
        recovering = false;
      }
    };
    // Snapshot pump: pull UI state + poses at 4Hz.
    const pump = async () => {
      if (!handle || recovering) return;
      try {
        const snap = await handle.client.snapshot();
        const decoded = decodeEntityBuffer(snap.entities as Uint8Array);
        const mgmt = decoded.ui.hud as UistMgmt | undefined;
        const detail = decoded.ui.mgmt as UistMgmtDetail | undefined;
        const policy = decoded.ui['zz-alert-policy'] as UiState['alertPolicy'];
        const journal = decoded.ui.journal as UiState['fieldJournal'];
        const extra = { observe: decoded.ui.observe as UiObserve | undefined, expedition: decoded.ui.expedition as UiExpedition | undefined, hunts: normHunts(decoded.ui.hunts), dashboard: decoded.ui.dashboard as UiDashboard | undefined, neighbors: decoded.ui.neighbors as UiNeighbors | undefined, poaching: decoded.ui.poaching as UiPoaching | undefined, outcome: (decoded.ui.field as { lastMinigameOutcome?: UiMinigameOutcome | null } | undefined)?.lastMinigameOutcome, capture: decoded.ui.field as UiFieldCapture | undefined, parkTraps: (decoded.ui.park as { traps?: UiTrap[] } | undefined)?.traps, facilities: (decoded.ui.facilities as { built?: string[]; catalog?: UiFacilityDef[] } | undefined)?.built, staffBeds: (decoded.ui.facilities as { staffBeds?: number } | undefined)?.staffBeds, facilityCatalog: (decoded.ui.facilities as { catalog?: UiFacilityDef[] } | undefined)?.catalog, financeDepth: decoded.ui.finance as UiFinanceDepth | undefined, studbook: decoded.ui.studbook as UiStudbook | undefined, priceHistory: decoded.ui['price-history'] as UiPriceHistory | undefined, story: decoded.ui.story as UiStory | undefined, lifelog: decoded.ui.lifelog as UiLifeLog | undefined };
        const overlay = decoded.ui.overlay as { cells?: Array<{ forageKg: number | null }> } | undefined;
        const minigames = (decoded.ui.field as { minigames?: UiMinigame[] } | undefined)?.minigames;
        if (mgmt) {
          scenarioActive = mgmt.mode !== null;
          state = { ...mapUiState(mgmt, state, detail, overlay, minigames, extra), alertPolicy: policy ?? null, fieldJournal:journal ?? null };
          if (pinnedTick !== null) { state = { ...state, tick: pinnedTick }; }
          lastPollAt = performance.now();
          emit();
        }
        pumpFailures = 0;
      } catch {
        // Worker not ready yet; next pump retries. Sustained failure means
        // a wedged worker: recreate + restore rather than show a dead sim.
        pumpFailures += 1;
        if (pumpFailures >= 8) void recoverWorker(); // recoverWorker self-guards reentry
      }
    };
    window.setInterval(() => { void pump(); }, 250);
  })();

  return {
    getState: () => state,
    async exportCurrentSave() {
      if(!ready||!handle||!scenarioActive)throw new Error('The current game is not ready to export.');
      return handle.client.exportCurrent(Date.now());
    },
    exportPreviousGame() { return latestReplacementSave(localStorage); },
    listSaveSlots() { if (!ready || !handle) return []; return handle.client.listSlots(); },
    async saveToSlot(slot) {
      if(!ready||!handle||!scenarioActive)throw new Error('The current game is not ready to save.');
      if(state.savingDisabled)throw new Error('Saving is off to protect your original save files.');
      await handle.client.save(slot, Date.now());
    },
    async loadFromSlot(slot) {
      if(!ready||!handle)throw new Error('The game is not ready to load.');
      // Protect live progress before replacing it: the autosave slot's whole
      // job is holding the latest live state, so a manual load never strands it.
      if(scenarioActive){const checkpoint=await handle.client.exportCurrent(Date.now());if(!preserveReplacementSave(localStorage,checkpoint))throw new Error('Could not protect the current game. Nothing was loaded.');}
      await handle.client.load(slot,Date.now());
    },
    async restoreSlotBackup(slot) {
      if(!ready||!handle)throw new Error('The game is not ready to load.');
      if(scenarioActive){const checkpoint=await handle.client.exportCurrent(Date.now());if(!preserveReplacementSave(localStorage,checkpoint))throw new Error('Could not protect the current game. Nothing was loaded.');}
      handle.client.restoreBackup(slot);
      await handle.client.load(slot,Date.now());
    },
    async importSaveFile(text) {
      if(!ready||!handle)throw new Error('The game is not ready to import.');
      if(state.savingDisabled)throw new Error('Saving is off to protect your original save files.');
      if(scenarioActive){const checkpoint=await handle.client.exportCurrent(Date.now());if(!preserveReplacementSave(localStorage,checkpoint))throw new Error('Could not protect the current game. Nothing was imported.');}
      const res=await handle.client.importEnvelope(text,Date.now()); // worker validates atomically: a bad file changes nothing
      await handle.client.save(0,Date.now()); // persist the imported game so unload cannot strand it
      return{tick:res.tick};
    },
    isLive: () => ready,
    tickFloat() {
      if (pinnedTick !== null) { return pinnedTick; }
      const elapsed = (performance.now() - lastPollAt) / 1000;
      return state.tick + elapsed * TICKS_PER_SECOND_1X * state.speed;
    },
    subscribe(fn) { subs.add(fn); return () => subs.delete(fn); },
    dismissAwayNotice() { if (state.savingDisabled) return; state = { ...state, awayNotice: null }; emit(); },
    sendTracked(type, payload) {
      return new Promise<string | null>((resolve, reject) => {
        whenReady(() => {
          const wire = COMMAND_ALIASES[type] ?? type;
          if (!handle) { reject(new Error('The worker is not ready.')); return; }
          handle.client.command(wire, payload, 'player').then((id) => { markDirty(); resolve(id); }, (err: unknown) => {
            const reason = err instanceof Error && err.message ? `: ${err.message}` : '';
            { const msg = `command_rejected: ${wire}${reason}`; state = { ...state, alerts: [...state.alerts, msg], alertEvents: [...state.alertEvents, { day: Math.floor(state.tick / 24), type: 'command_rejected', refId: null, message: msg }] }; }
            emit();
            reject(err instanceof Error ? err : new Error(String(err)));
          });
        });
      });
    },
    send(type, payload) {
      if (type === 'time.setSpeed') {
        const speed = (payload as { speed: Speed }).speed;
        state = { ...state, speed };
        lastPollAt = performance.now();
        emit();
        whenReady(() => { handle?.client.setSpeed(speed); });
        markDirty();
        return;
      }
      whenReady(() => {
        // An explicit scenario.start (difficulty picker) disarms the
        // auto-standard guard below and goes straight to the worker.
        if (type === 'scenario.start') { scenarioStarted = true; }
        // Choosing a ranch path starts the scenario first (balance.json
        // start cash/animals per mode); without it the economy sits at $0.
        if (type === 'ranch.setPath' && payload && (payload as { path?: string }).path === 'ranch') {
          payload = { ...payload, path: 'open_ranch' };
        }
        if (type === 'ranch.setPath' && !scenarioStarted) {
          scenarioStarted = true;
          handle?.client.command('scenario.start', { mode: 'standard' }, 'player').catch(() => {
            scenarioStarted = false; // allow retry if the boundary rejected it
          });
        }
        const wire = COMMAND_ALIASES[type] ?? type;
        handle?.client.command(wire, payload, 'player').then(() => { markDirty(); }).catch((err: unknown) => {
          // No fake success: a rejected command surfaces as a visible alert
          // with the boundary's reason when it gives one.
          const reason = err instanceof Error && err.message ? `: ${err.message}` : '';
          { const msg = `command_rejected: ${wire}${reason}`; state = { ...state, alerts: [...state.alerts, msg], alertEvents: [...state.alertEvents, { day: Math.floor(state.tick / 24), type: 'command_rejected', refId: null, message: msg }] }; }
          // A REJECTED explicit start must not leave the heal disarmed:
          // release the latch so the self-heal (or a retry) can still re-arm
          // the session. Mirrors the auto-standard reset below.
          if (type === 'scenario.start') { scenarioStarted = false; }
          emit();
        });
      });
    },
    monetization: {
      onEvent() { /* hooks only - nothing monetized */ },
      isPremiumEntitlementActive() { return false; },
    },
  };
}
